import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import {
  FaChevronDown,
  FaArrowLeft,
  FaArrowRight,
  FaCloudUploadAlt,
  FaVideo,
  FaImage,
  FaEye,
  FaGlobeAmericas,
  FaCity,
  FaChartLine,
  FaClock,
  FaPlayCircle,
  FaWallet,
  FaShieldAlt,
  FaCheckCircle,
  FaInfoCircle,
  FaGlobe,
  FaCalendarAlt,
  FaRulerHorizontal,
  FaUsers,
  FaUserFriends,
  FaChartBar,
  FaMapMarkerAlt,
  FaTicketAlt,
  FaCreditCard,
  FaDownload
} from "react-icons/fa";
import stateCityData from "../../../data/states.json";

// Get API URL from environment variables
const baseUrl =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_RENDER_URL ||
  "http://localhost:5005";

// Helper Components
const FormSection = ({ title, children }) => (
  <div className="p-4 border rounded-lg shadow-md bg-white space-y-4">
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    {children}
  </div>
);

// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  onWalletPay,
  formData,
  plateforms,
  totalCost,
  totalCostWithGST,
  savedAmount,
  gstRate,
  appliedCoupon,
  imageSrc,
  videoSrc,
  isPostingAd,
  setFullScreenMedia,
  setIsMediaFullScreen,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-100">
        {isPostingAd ? (
          <div className="p-8 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-blue-600 mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Posting Your Advertisement
            </h2>
            <p className="text-gray-600 text-center">
              Please wait while we process your advertisement...
            </p>
          </div>
        ) : (
          <div className="p-8">
            {/* Header with gradient */}
            <div className="text-center mb-8 bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-xl shadow-lg">
              <h2 className="text-3xl font-bold text-white mb-2">
                Confirm Advertisement Details
              </h2>
              <p className="text-blue-100">Review your advertisement before submission</p>
            </div>

            <div className="space-y-6 mb-8">
              {/* Advertisement Information Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-xl mb-4 text-blue-800 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Advertisement Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {formData.channelType.length > 0 && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <span className="font-semibold text-blue-700 block mb-2">Channels:</span>
                      <ul className="space-y-1">
                        {formData.channelType.map((channel, index) => (
                          <li key={index} className="flex items-center text-gray-700">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                            {channel}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {formData.plateforms && formData.plateforms.length > 0 && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <span className="font-semibold text-blue-700 block mb-2">Platforms:</span>
                      <ul className="space-y-1">
                        {formData.plateforms.map((platform, index) => (
                          <li key={index} className="flex items-center text-gray-700">
                            <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                            {platform}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {formData.userType && (
                    <div className="bg-white rounded-lg p-4 shadow-sm relative overflow-hidden group">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-semibold text-blue-700">Target User Type:</span>
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${formData.userType === "reporter"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-purple-100 text-purple-800"
                            }`}>
                            {formData.userType === "reporter" ? "Reporter" : "Influencer"}
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 flex items-start gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1"></div>
                        <p className="text-[10px] font-bold text-slate-500 italic">
                          Note: No {formData.userType === "reporter" ? "Reporter" : "Influencer"} report will share this estimated reach with third parties.
                        </p>
                      </div>
                    </div>
                  )}

                  {formData.adType && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <span className="font-semibold text-blue-700">Ad Type:</span>
                      <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {formData.adType}
                      </span>
                    </div>
                  )}

                  {formData.startDate && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <span className="font-semibold text-blue-700">Start Date:</span>
                      <span className="ml-2 text-gray-700">{formData.startDate}</span>
                    </div>
                  )}


                  {formData.requiredViews && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <span className="font-semibold text-blue-700">Views:</span>
                      <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        {formData.requiredViews}
                      </span>
                    </div>
                  )}

                  {formData.adLength && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <span className="font-semibold text-blue-700">Duration:</span>
                      <span className="ml-2 bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                        {formData.adLength}s
                      </span>
                    </div>
                  )}
                </div>
              </div>


              {/* Media Information Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-xl mb-4 text-purple-800 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  Media Information
                </h3>

                {/* Media Preview */}
                {(imageSrc || videoSrc) && (
                  <div className="mb-6">
                    <div 
                      className="group relative border-2 border-dashed border-purple-300 rounded-xl overflow-hidden bg-white shadow-lg cursor-pointer"
                      onClick={() => {
                        setFullScreenMedia({ src: imageSrc || videoSrc, type: formData.mediaType });
                        setIsMediaFullScreen(true);
                      }}
                    >
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 z-10 bg-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-white/20 backdrop-blur-md p-3 rounded-full border border-white/30 transform scale-90 group-hover:scale-100 transition-transform">
                          <FaEye className="w-6 h-6 text-white" />
                        </div>
                      </div>

                      {imageSrc && formData.mediaType === "image" && (
                        <img
                          src={imageSrc}
                          alt="Ad Preview"
                          className="w-full h-auto max-h-64 object-contain transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                      {videoSrc && formData.mediaType === "video" && (
                        <video
                          src={videoSrc}
                          className="w-full h-auto max-h-64"
                        />
                      )}
                    </div>
                    <p className="text-[10px] text-center text-purple-400 font-bold uppercase tracking-widest mt-2">Click to view full size</p>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <span className="font-semibold text-purple-700">Media Type:</span>
                    <span className="ml-2 bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium capitalize">
                      {formData.mediaType}
                    </span>
                  </div>

                  {formData.mediaType === "video" && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <span className="font-semibold text-purple-700">Duration:</span>
                      <span className="ml-2 text-gray-700 font-medium">{formData.adLength}s</span>
                    </div>
                  )}

                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <span className="font-semibold text-purple-700">File Name:</span>
                    <span className="ml-2 text-gray-700 font-medium truncate inline-block max-w-[150px]">
                      {formData.mediaType === "video" ? formData.video?.name || "Video File" : formData.image?.name || "Image File"}
                    </span>
                  </div>

                  {formData.mediaDescription && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <span className="font-semibold text-purple-700 block mb-2">Media Description:</span>
                      <p className="text-gray-700 text-sm bg-gray-50 p-3 rounded-lg">
                        {formData.mediaDescription}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Details Section */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-xl mb-4 text-yellow-800 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zM14 6a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h8zM6 8a2 2 0 012 2v1a2 2 0 01-2 2H4v-1a2 2 0 012-2h0z" />
                  </svg>
                  Payment Details
                </h3>
                <div className="bg-white rounded-lg p-4 shadow-sm space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold text-gray-800">₹{totalCost.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">GST ({gstRate}%):</span>
                    <span className="font-semibold text-gray-800">₹{((totalCost * gstRate) / 100).toFixed(2)}</span>
                  </div>

                  {appliedCoupon && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-green-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                        </svg>
                        Coupon Discount ({" "}
                        {appliedCoupon.type === "percentage"
                          ? `${appliedCoupon.discount}% off`
                          : `₹${appliedCoupon.discount} off`}
                        ):
                      </span>
                      <span className="font-semibold text-green-600">-₹{savedAmount.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg px-4 mt-4">
                    <span className="font-bold text-lg">Total Amount:</span>
                    <span className="font-bold text-2xl">
                      ₹{totalCostWithGST.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-8 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all duration-200 border border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md"
              >
                ← Go Back
              </button>
              <button
                onClick={onConfirm}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Confirm & Submit
              </button>
              <button
                onClick={onWalletPay}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                💳 Pay from Wallet
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Mobile Price Strip will be rendered inside PostAdvertisement component */}
    </div>
  );
};

// ─── Real Leaflet Map ──────────────────────────────────────────────────────
const CITY_LATLNG = {
  // Maharashtra
  "Mumbai": [19.076, 72.877], "Pune": [18.520, 73.856], "Nagpur": [21.145, 79.089],
  "Nashik": [19.997, 73.790], "Aurangabad": [19.877, 75.343], "Solapur": [17.686, 75.906],
  "Thane": [19.218, 72.978], "Kolhapur": [16.705, 74.243], "Amravati": [20.932, 77.750],
  "Nanded": [19.160, 77.308], "Sangli": [16.856, 74.563], "Jalgaon": [21.004, 75.562],
  "Akola": [20.706, 77.003], "Latur": [18.406, 76.560], "Dhule": [20.901, 74.777],
  "Ahmednagar": [19.094, 74.739], "Chandrapur": [19.960, 79.296], "Parbhani": [19.274, 76.775],
  // Delhi & NCR
  "Delhi": [28.614, 77.209], "New Delhi": [28.614, 77.209], "Gurgaon": [28.459, 77.028],
  "Noida": [28.535, 77.391], "Faridabad": [28.408, 77.317], "Ghaziabad": [28.670, 77.454],
  // Haryana — complete list
  "Chandigarh": [30.734, 76.779], "Hisar": [29.151, 75.722], "Rohtak": [28.895, 76.606],
  "Ambala": [30.378, 76.777], "Jhajjar": [28.608, 76.655], "Bhiwani": [28.791, 76.132],
  "Yamunanagar": [30.128, 77.287], "Panipat": [29.391, 76.967], "Karnal": [29.686, 76.989],
  "Sonipat": [28.993, 77.021], "Rewari": [28.192, 76.618], "Kaithal": [29.801, 76.399],
  "Sirsa": [29.533, 75.023], "Fatehabad": [29.519, 75.455], "Mahendragarh": [28.280, 76.145],
  "Jind": [29.316, 76.316], "Kurukshetra": [29.959, 76.878], "Panchkula": [30.695, 76.853],
  "Bahadurgarh": [28.692, 76.929], "Thanesar": [29.959, 76.822], "Palwal": [28.143, 77.330],
  "Hansi": [29.100, 75.965], "Narnaul": [28.045, 76.109], "Gohana": [29.138, 76.703],
  "Tohana": [29.716, 75.913], "Narwana": [29.603, 76.115], "Mandi Dabwali": [29.761, 74.730],
  "Charkhi Dadri": [28.591, 76.271], "Shahbad": [30.163, 76.895], "Pehowa": [29.983, 76.577],
  "Samalkha": [29.236, 77.013], "Pinjore": [30.797, 76.900], "Sohna": [28.248, 77.066],
  "Safidon": [29.383, 76.664], "Ratia": [29.686, 75.567], "Rania": [29.545, 74.848],
  "Ladwa": [29.994, 77.029], "Taraori": [29.795, 76.883], "Sarsod": [29.300, 76.750],
  // Karnataka
  "Bengaluru": [12.972, 77.594], "Mysuru": [12.296, 76.639], "Mysore": [12.296, 76.639],
  "Mangaluru": [12.914, 74.856], "Hubli": [15.348, 75.135], "Hubli-Dharwad": [15.360, 75.120],
  "Belagavi": [15.849, 74.497], "Davanagere": [14.465, 75.921], "Ballari": [15.141, 76.924],
  "Tumkur": [13.342, 77.101], "Shivamogga": [13.930, 75.568], "Kolar": [13.135, 78.130],
  "Mandya": [12.526, 76.895], "Udupi": [13.341, 74.742], "Ramanagaram": [12.721, 77.286],
  "Vijayapura": [16.830, 75.715], "Raayachuru": [16.483, 77.350], "Gokak": [16.167, 74.819],
  "Robertson Pet": [12.961, 78.221], "Ranebennuru": [14.628, 75.628], "Yadgir": [16.765, 77.138],
  "Ranibennur": [14.628, 75.628], "Ramanagaram": [12.721, 77.286], "Sagara": [14.170, 75.028],
  // Tamil Nadu
  "Chennai": [13.083, 80.270], "Coimbatore": [11.017, 76.955], "Madurai": [9.917, 78.119],
  "Tiruchirappalli": [10.805, 78.686], "Salem": [11.664, 78.147], "Tirunelveli": [8.713, 77.756],
  "Tiruppur": [11.108, 77.341], "Ranipet": [12.929, 79.331], "Nagercoil": [8.178, 77.435],
  "Thanjavur": [10.787, 79.138], "Vellore": [12.916, 79.133], "Kancheepuram": [12.836, 79.699],
  "Erode": [11.341, 77.717], "Tiruvannamalai": [12.225, 79.073], "Virudhunagar": [9.581, 77.951],
  "Karur": [10.957, 78.082], "Ramanathapuram": [9.371, 78.831], "Namakkal": [11.221, 78.167],
  "Sivakasi": [9.454, 77.796], "Pudukkottai": [10.379, 78.820], "Nagapattinam": [10.765, 79.843],
  "Dindigul": [10.364, 77.975], "Kumbakonam": [10.961, 79.408], "Cuddalore": [11.750, 79.768],
  // Uttar Pradesh
  "Lucknow": [26.847, 80.947], "Kanpur": [26.449, 80.331], "Agra": [27.180, 78.021],
  "Varanasi": [25.317, 82.973], "Allahabad": [25.435, 81.846], "Prayagraj": [25.435, 81.846],
  "Meerut": [28.987, 77.709], "Bareilly": [28.347, 79.415], "Aligarh": [27.881, 78.082],
  "Moradabad": [28.838, 78.776], "Saharanpur": [29.968, 77.546], "Gorakhpur": [26.760, 83.373],
  "Firozabad": [27.149, 78.395], "Jhansi": [25.448, 78.568], "Muzaffarnagar": [29.473, 77.697],
  "Mathura": [27.492, 77.673], "Rampur": [28.802, 79.004], "Shahjahanpur": [27.882, 79.907],
  "Ayodhya": [26.795, 82.204], "Faizabad": [26.774, 82.148], "Ghazipur": [25.579, 83.579],
  "Mirzapur": [25.138, 82.563], "Hapur": [28.734, 77.782], "Etawah": [26.786, 79.022],
  "Bulandshahr": [28.406, 77.850], "Lakhimpur": [27.949, 80.781], "Gonda": [27.134, 81.964],
  "Azamgarh": [26.067, 83.184], "Ballia": [25.760, 84.147], "Jaunpur": [25.748, 82.684],
  "Sitapur": [27.564, 80.683], "Bahraich": [27.574, 81.598], "Deoria": [26.502, 83.785],
  // Gujarat
  "Ahmedabad": [23.023, 72.572], "Surat": [21.170, 72.831], "Vadodara": [22.307, 73.181],
  "Rajkot": [22.300, 70.800], "Bhavnagar": [21.766, 72.151], "Jamnagar": [22.471, 70.058],
  "Junagadh": [21.524, 70.458], "Gandhinagar": [23.216, 72.684], "Anand": [22.557, 72.952],
  "Morbi": [22.817, 70.832], "Navsari": [20.951, 72.921], "Mehsana": [23.600, 72.375],
  "Surendranagar": [22.727, 71.647], "Bharuch": [21.706, 72.998], "Porbandar": [21.644, 69.601],
  "Gandhidham": [23.080, 70.130], "Ankleshwar": [21.627, 73.018], "Valsad": [20.588, 72.928],
  // Rajasthan
  "Jaipur": [26.913, 75.787], "Jodhpur": [26.292, 73.017], "Udaipur": [24.571, 73.686],
  "Kota": [25.182, 75.865], "Ajmer": [26.450, 74.637], "Bikaner": [28.022, 73.312],
  "Alwar": [27.564, 76.616], "Bharatpur": [27.215, 77.503], "Sikar": [27.612, 75.139],
  "Barmer": [25.751, 71.395], "Nagaur": [27.202, 73.738], "Tonk": [26.167, 75.790],
  "Bhilwara": [25.346, 74.635], "Pali": [25.773, 73.324], "Churu": [28.298, 74.959],
  "Sri Ganganagar": [29.917, 73.877], "Hanumangarh": [29.583, 74.327],
  "Sawai Madhopur": [26.000, 76.356], "Makrana": [27.038, 74.729],
  // West Bengal
  "Kolkata": [22.573, 88.364], "Howrah": [22.589, 88.311], "Durgapur": [23.480, 87.320],
  "Asansol": [23.679, 86.956], "Siliguri": [26.717, 88.428], "Bardhaman": [23.233, 87.856],
  "Malda": [25.003, 88.144], "Baharampur": [24.107, 88.252], "Kharagpur": [22.329, 87.318],
  // Madhya Pradesh
  "Bhopal": [23.259, 77.413], "Indore": [22.719, 75.857], "Jabalpur": [23.182, 79.987],
  "Gwalior": [26.219, 78.182], "Ujjain": [23.184, 75.784], "Sagar": [23.841, 78.740],
  "Ratlam": [23.333, 75.040], "Satna": [24.601, 80.830], "Rewa": [24.531, 81.300],
  "Morena": [26.503, 77.999], "Singrauli": [24.200, 82.661], "Vidisha": [23.523, 77.805],
  "Shivpuri": [25.423, 77.660], "Mandsaur": [24.073, 75.071], "Neemuch": [24.473, 74.869],
  "Itarsi": [22.617, 77.760], "Seoni": [22.088, 79.543], "Shahdol": [23.294, 81.358],
  // Telangana
  "Hyderabad": [17.387, 78.491], "Warangal": [17.978, 79.598], "Nizamabad": [18.673, 78.094],
  "Khammam": [17.247, 80.151], "Karimnagar": [18.438, 79.129], "Ramagundam": [18.754, 79.474],
  "Mahabubnagar": [16.737, 77.983], "Nalgonda": [17.054, 79.268], "Adilabad": [19.664, 78.528],
  // Andhra Pradesh
  "Visakhapatnam": [17.686, 83.218], "Vijayawada": [16.506, 80.648], "Guntur": [16.307, 80.436],
  "Nellore": [14.411, 79.987], "Kurnool": [15.828, 77.999], "Rajahmundry": [16.992, 81.785],
  "Kakinada": [16.939, 82.237], "Tirupati": [13.629, 79.419], "Anantapur": [14.682, 77.601],
  "Kadapa": [14.474, 78.823], "Vizianagaram": [18.116, 83.412], "Eluru": [16.713, 81.095],
  "Ongole": [15.503, 80.048], "Machilipatnam": [16.187, 81.136], "Chittoor": [13.215, 79.100],
  "Hindupur": [13.829, 77.491], "Bhimavaram": [16.544, 81.521], "Tenali": [16.241, 80.640],
  // Kerala
  "Thiruvananthapuram": [8.524, 76.936], "Kochi": [9.931, 76.267], "Kozhikode": [11.259, 75.780],
  "Kollam": [8.887, 76.614], "Thrissur": [10.526, 76.214], "Palakkad": [10.776, 76.654],
  "Alappuzha": [9.498, 76.339], "Malappuram": [11.041, 76.073], "Kannur": [11.868, 75.370],
  "Kottayam": [9.591, 76.522], "Kasaragod": [12.499, 74.990], "Tirur": [10.912, 75.923],
  "Vatakara": [11.605, 75.590], "Guruvayoor": [10.595, 76.041], "Ponnani": [10.769, 75.990],
  // Punjab
  "Ludhiana": [30.901, 75.857], "Amritsar": [31.634, 74.873], "Jalandhar": [31.326, 75.576],
  "Patiala": [30.339, 76.386], "Bathinda": [30.211, 74.943], "Pathankot": [32.274, 75.652],
  "Hoshiarpur": [31.531, 75.912], "Moga": [30.815, 75.169], "Mohali": [30.704, 76.718],
  "Firozpur": [30.927, 74.609], "Phagwara": [31.225, 75.769], "Kapurthala": [31.380, 75.380],
  "Sangrur": [30.244, 75.843], "Barnala": [30.378, 75.549], "Muktsar": [30.478, 74.514],
  "Gurdaspur": [32.038, 75.408], "Rupnagar": [30.966, 76.525], "Nabha": [30.377, 76.141],
  "Malerkotla": [30.529, 75.880], "Batala": [31.819, 75.203], "Mansa": [29.998, 75.397],
  "Zirakpur": [30.642, 76.817], "Faridkot": [30.675, 74.755], "Khanna": [30.705, 76.219],
  // Bihar
  "Patna": [25.596, 85.138], "Gaya": [24.797, 84.999], "Bhagalpur": [25.244, 87.007],
  "Muzaffarpur": [26.120, 85.364], "Purnia": [25.778, 87.470], "Darbhanga": [26.152, 85.897],
  "Bihar Sharif": [25.198, 85.523], "Begusarai": [25.418, 86.127], "Katihar": [25.542, 87.573],
  "Munger": [25.371, 86.474], "Hajipur": [25.690, 85.208], "Arrah": [25.556, 84.663],
  "Sasaram": [24.947, 84.030], "Chapra": [25.778, 84.745],
  // Odisha
  "Bhubaneswar": [20.296, 85.824], "Cuttack": [20.462, 85.882], "Raurkela": [22.220, 84.852],
  "Brahmapur": [19.310, 84.794], "Sambalpur": [21.468, 83.977], "Puri": [19.810, 85.832],
  "Baleshwar Town": [21.493, 86.933], "Baripada Town": [21.932, 86.726], "Jharsuguda": [21.862, 84.007],
  "Bargarh": [21.336, 83.624], "Bhadrak": [21.054, 86.505], "Balangir": [20.710, 83.484],
  // Assam
  "Guwahati": [26.145, 91.745], "Silchar": [24.827, 92.798], "Dibrugarh": [27.479, 94.915],
  "Nagaon": [26.346, 92.686], "Tinsukia": [27.493, 95.356], "Jorhat": [26.751, 94.197],
  "Tezpur": [26.633, 92.800], "Karimganj": [24.866, 92.355], "Diphu": [25.842, 93.432],
  "North Lakhimpur": [27.234, 94.107], "Barpeta": [26.323, 91.003], "Nalbari": [26.447, 91.441],
  "Bongaigaon City": [26.477, 90.559], "Dhubri": [26.022, 89.990], "Goalpara": [26.172, 90.612],
  // Uttarakhand
  "Dehradun": [30.316, 78.032], "Hardwar": [29.945, 78.161], "Haridwar": [29.945, 78.161],
  "Haldwani-cum-Kathgodam": [29.219, 79.529], "Roorkee": [29.867, 77.889],
  "Rudrapur": [28.988, 79.397], "Rishikesh": [30.087, 78.268], "Kashipur": [29.212, 78.963],
  "Ramnagar": [29.395, 79.120], "Nainital": [29.381, 79.461], "Mussoorie": [30.454, 78.064],
  "Pithoragarh": [29.582, 80.218], "Sitarganj": [28.927, 79.693],
  // Jharkhand
  "Ranchi": [23.344, 85.310], "Jamshedpur": [22.804, 86.203], "Dhanbad": [23.795, 86.430],
  "Bokaro Steel City": [23.667, 85.982], "Bokaro": [23.667, 85.982],
  "Deoghar": [24.486, 86.695], "Hazaribag": [23.993, 85.358], "Giridih": [24.196, 86.303],
  "Ramgarh": [23.634, 85.514], "Dumka": [24.269, 87.246], "Chaibasa": [22.554, 85.801],
  "Phusro": [23.770, 86.243], "Adityapur": [22.780, 86.214], "Lohardaga": [23.441, 84.690],
  "Chatra": [24.212, 84.874], "Pakaur": [24.635, 87.842], "Simdega": [22.612, 84.502],
  // Chhattisgarh
  "Raipur": [21.250, 81.630], "Bhilai Nagar": [21.209, 81.428], "Korba": [22.345, 82.686],
  "Bilaspur": [22.090, 82.148], "Durg": [21.189, 81.283], "Rajnandgaon": [21.097, 81.030],
  "Jagdalpur": [19.082, 82.020], "Raigarh": [21.900, 83.397], "Ambikapur": [23.117, 83.197],
  "Mahasamund": [21.107, 82.097], "Dhamtari": [20.707, 81.549],
  // Himachal Pradesh
  "Shimla": [31.104, 77.173], "Mandi": [31.709, 76.931], "Solan": [30.907, 77.096],
  "Nahan": [30.558, 77.295], "Palampur": [32.110, 76.540], "Kullu": [31.958, 77.109],
  "Sundarnagar": [31.534, 76.895],
  // Jammu & Kashmir / J&K
  "Srinagar": [34.083, 74.797], "Jammu": [32.726, 74.857], "Baramula": [34.197, 74.342],
  "Anantnag": [33.731, 75.154], "Sopore": [34.299, 74.472], "Udhampur": [32.916, 75.139],
  "Rajauri": [33.377, 74.309], "Punch": [33.773, 74.095],
  // Goa
  "Panaji": [15.498, 73.827], "Margao": [15.278, 73.958], "Mapusa": [15.593, 73.808],
  "Ponda": [15.403, 74.010], "Vasco da Gama": [15.395, 73.814],
  // Mizoram
  "Aizawl": [23.726, 92.718], "Lunglei": [22.887, 92.734], "Saiha": [22.489, 92.973],
  // Nagaland
  "Dimapur": [25.909, 93.722], "Kohima": [25.674, 94.110], "Mokokchung": [26.325, 94.516],
  "Zunheboto": [26.006, 94.512], "Tuensang": [26.272, 94.819], "Wokha": [26.104, 94.261],
  // Manipur
  "Imphal": [24.817, 93.942], "Thoubal": [24.643, 93.990], "Lilong": [24.727, 93.934],
  // Tripura
  "Agartala": [23.836, 91.280], "Dharmanagar": [24.374, 92.175], "Kailasahar": [24.319, 92.001],
  "Belonia": [23.247, 91.449], "Khowai": [24.066, 91.606],
  // Andaman
  "Port Blair": [11.662, 92.746],
  // Puducherry
  "Pondicherry": [11.934, 79.830], "Karaikal": [10.924, 79.836], "Yanam": [16.731, 82.213],
  // Others
  "Silvassa": [20.274, 72.996], "Chandigarh UT": [30.734, 76.779],
};

const STATE_CENTER = {
  "Maharashtra": [19.7515, 75.7139], "Karnataka": [15.3173, 75.7139],
  "Delhi": [28.7041, 77.1025], "Uttar Pradesh": [26.8467, 80.9462],
  "Gujarat": [22.2587, 71.1924], "Tamil Nadu": [11.1271, 78.6569],
  "West Bengal": [22.9868, 87.8550], "Rajasthan": [27.0238, 74.2179],
  "Madhya Pradesh": [22.9734, 78.6569], "Telangana": [18.1124, 79.0193],
  "Andhra Pradesh": [15.9129, 79.7400], "Kerala": [10.8505, 76.2711],
  "Punjab": [31.1471, 75.3412], "Haryana": [29.0588, 76.0856],
  "Bihar": [25.0961, 85.3131], "Odisha": [20.9517, 85.0985],
  "Chhattisgarh": [21.2787, 81.8661], "Jharkhand": [23.6102, 85.2799],
  "Assam": [26.2006, 92.9376], "Uttarakhand": [30.0668, 79.0193],
  "Himachal Pradesh": [31.1048, 77.1734], "Jammu & Kashmir": [33.7782, 76.5762],
  "Goa": [15.2993, 74.1240],
};

// ─── Premium Map Styles ─────────────────────────────────────────────────────
const MAP_CSS = `
  @keyframes cityGlow {
    0%,100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.8), 0 0 6px rgba(52,211,153,0.5); }
    60%      { box-shadow: 0 0 0 10px rgba(52,211,153,0), 0 0 12px rgba(52,211,153,0.3); }
  }
  @keyframes stateGlow {
    0%,100% { box-shadow: 0 0 0 0 rgba(251,146,60,0.9), 0 0 8px rgba(251,146,60,0.5); }
    60%      { box-shadow: 0 0 0 14px rgba(251,146,60,0), 0 0 14px rgba(251,146,60,0.3); }
  }
  @keyframes dashAnim {
    to { stroke-dashoffset: -30; }
  }
  .city-dot {
    width:13px; height:13px;
    background: linear-gradient(135deg,#34d399,#10b981);
    border:2.5px solid rgba(255,255,255,0.9);
    border-radius:50%;
    animation: cityGlow 2.2s ease-in-out infinite;
    cursor: pointer;
  }
  .state-dot {
    width:20px; height:20px;
    background: linear-gradient(135deg,#fb923c,#f97316);
    border:3px solid rgba(255,255,255,0.95);
    border-radius:50%;
    animation: stateGlow 1.8s ease-in-out infinite;
    cursor: pointer;
  }
  .leaflet-pane .city-line { animation: dashAnim 0.8s linear infinite; }
  .leaflet-tooltip {
    background: rgba(10,15,30,0.95) !important;
    border: 1px solid rgba(52,211,153,0.5) !important;
    color: #a7f3d0 !important;
    font-weight: 800 !important;
    font-size: 10px !important;
    border-radius: 8px !important;
    box-shadow: 0 0 20px rgba(16,185,129,0.4), 0 4px 12px rgba(0,0,0,0.6) !important;
    padding: 4px 10px !important;
    letter-spacing: 0.05em;
    backdrop-filter: blur(8px);
  }
  .leaflet-tooltip::before,
  .leaflet-tooltip-top::before { display:none !important; }
  .leaflet-control-zoom {
    border: 1px solid rgba(52,211,153,0.3) !important;
    box-shadow: 0 4px 16px rgba(0,0,0,0.6) !important;
    border-radius: 10px !important;
    overflow: hidden;
  }
  .leaflet-control-zoom a {
    background: rgba(10,15,30,0.95) !important;
    color: #34d399 !important;
    border-color: rgba(52,211,153,0.2) !important;
    font-weight: 900;
    font-size: 16px;
  }
  .leaflet-control-zoom a:hover {
    background: rgba(52,211,153,0.2) !important;
    color: #fff !important;
  }
  .leaflet-state-tooltip {
    background: rgba(251,146,60,0.15) !important;
    border: 1px solid rgba(251,146,60,0.6) !important;
    color: #fed7aa !important;
  }
  .leaflet-city-tooltip {
    background: rgba(52,211,153,0.1) !important;
    border: 1px solid rgba(52,211,153,0.4) !important;
    color: #a7f3d0 !important;
    font-size: 8px !important;
    padding: 2px 6px !important;
  }
`;

let mapCSSInjected = false;
function injectMapCSS() {
  if (mapCSSInjected || document.getElementById('iinsaf-map-css')) return;
  const style = document.createElement('style');
  style.id = 'iinsaf-map-css';
  style.textContent = MAP_CSS;
  document.head.appendChild(style);
  mapCSSInjected = true;
}

const TILE_LAYERS = {
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    label: '🌙 Dark',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    label: '🛰 Satellite',
  },
  terrain: {
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    label: '🗺 Terrain',
  },
};

// FitBounds — child of MapContainer
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      map.flyToBounds(points, { padding: [48, 48], maxZoom: 9, duration: 1.2 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points)]);
  return null;
};

const IndiaMap = ({ selectedStates = [], selectedCities = [] }) => {
  const [tileKey, setTileKey] = useState('dark');

  useEffect(() => { injectMapCSS(); }, []);

  const cityPoints = selectedCities.map(c => ({ name: c, pos: CITY_LATLNG[c] })).filter(p => p.pos);
  const statePoints = selectedStates.map(s => ({ name: s, pos: STATE_CENTER[s] })).filter(p => p.pos);
  const allPoints = [...cityPoints, ...statePoints].map(p => p.pos);
  const cityPositions = cityPoints.map(c => c.pos);
  const stateToCity = statePoints.flatMap(sp => cityPoints.map(cp => [sp.pos, cp.pos]));

  const estReach = cityPoints.length * 120000 + statePoints.length * 40000;
  const fmtReach = estReach >= 1000000
    ? `${(estReach / 1000000).toFixed(1)}M`
    : estReach >= 1000 ? `${(estReach / 1000).toFixed(0)}K` : estReach.toString();

  // Build DivIcon-style markers using SVG inside CircleMarker
  // (react-leaflet's Marker + L.divIcon would require fixing default icon path issues;
  //  using CircleMarker with large radius + inner ring instead for zero-dep approach)

  return (
    <div className="mt-4 rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(16,185,129,0.25)] relative"
      style={{ border: '1px solid rgba(52,211,153,0.35)' }}>

      {/* Glowing top edge */}
      <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, #10b981, #34d399, #10b981, transparent)' }} />

      {/* Header */}
      <div className="bg-[#0a0f1e] px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(52,211,153,0.12)' }}>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{ boxShadow: '0 0 8px #10b981' }} />
          <span className="text-xs font-black text-white tracking-wide">
            {statePoints.length > 0
              ? <>{statePoints.length} STATE{statePoints.length > 1 ? 'S' : ''}<span className="text-emerald-400"> · </span>{cityPoints.length} CITIES<span className="text-emerald-400"> · </span>~{fmtReach} REACH</>
              : 'SELECT A STATE TO ACTIVATE MAP'}
          </span>
        </div>
        {/* Tile switcher */}
        <div className="flex gap-1">
          {Object.entries(TILE_LAYERS).map(([key, val]) => (
            <button key={key} type="button"
              onClick={() => setTileKey(key)}
              className={`px-2 py-0.5 text-[8px] font-black rounded-md transition-all ${tileKey === key
                ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-500/50'
                : 'text-white/30 hover:text-white/60 border border-white/10'
                }`}
            >{val.label}</button>
          ))}
        </div>
      </div>

      {/* Map */}
      <div className="relative">
        <MapContainer
          center={[20.5937, 78.9629]}
          zoom={4}
          style={{ height: 360, width: '100%' }}
          scrollWheelZoom={false}
          attributionControl={false}
          zoomControl
        >
          <TileLayer key={tileKey} url={TILE_LAYERS[tileKey].url} />

          {allPoints.length > 0 && <FitBounds points={allPoints} />}

          {/* Thicker glow line: city network */}
          {cityPositions.length > 1 && (
            <>
              {/* shadow line */}
              <Polyline positions={cityPositions}
                pathOptions={{ color: '#10b981', weight: 8, opacity: 0.12, lineJoin: 'round' }} />
              {/* main animated line */}
              <Polyline positions={cityPositions}
                pathOptions={{ color: '#34d399', weight: 2.5, opacity: 0.9, dashArray: '10 8', className: 'city-line' }} />
            </>
          )}

          {/* State → City spoke lines */}
          {stateToCity.map((pair, i) => (
            <React.Fragment key={i}>
              <Polyline positions={pair}
                pathOptions={{ color: '#fb923c', weight: 5, opacity: 0.08 }} />
              <Polyline positions={pair}
                pathOptions={{ color: '#f97316', weight: 1.5, opacity: 0.6, dashArray: '5 7' }} />
            </React.Fragment>
          ))}

          {/* State markers — outer glow ring + solid core */}
          {statePoints.map(sp => (
            <React.Fragment key={sp.name}>
              {/* outer glow ring */}
              <CircleMarker center={sp.pos} radius={22}
                pathOptions={{ color: '#fb923c', fillColor: '#fb923c', fillOpacity: 0.07, weight: 1, opacity: 0.4, dashArray: '3 3' }} />
              {/* mid ring */}
              <CircleMarker center={sp.pos} radius={13}
                pathOptions={{ color: '#f97316', fillColor: '#fb923c', fillOpacity: 0.25, weight: 1.5, opacity: 0.7 }} />
              {/* solid core */}
              <CircleMarker center={sp.pos} radius={7}
                pathOptions={{ color: '#fff', fillColor: '#f97316', fillOpacity: 1, weight: 2 }}
              >
                <Tooltip permanent direction="top" offset={[0, -12]}
                  className="leaflet-state-tooltip">
                  📍 {sp.name}
                </Tooltip>
              </CircleMarker>
            </React.Fragment>
          ))}

          {/* City markers — glowing dot */}
          {cityPoints.map((cp, idx) => (
            <React.Fragment key={cp.name}>
              {/* outer pulse ring */}
              <CircleMarker center={cp.pos} radius={14}
                pathOptions={{ color: '#34d399', fillColor: '#34d399', fillOpacity: 0.08, weight: 1, opacity: 0.35 }} />
              {/* solid dot */}
              <CircleMarker center={cp.pos} radius={6}
                pathOptions={{ color: '#fff', fillColor: '#34d399', fillOpacity: 1, weight: 1.5 }}
              >
                <Tooltip permanent direction="top" offset={[0, -8]} className="leaflet-city-tooltip">
                  🟢 {cp.name}
                </Tooltip>
              </CircleMarker>
            </React.Fragment>
          ))}
        </MapContainer>

        {/* Floating stats overlay — bottom-left inside map */}
        {statePoints.length > 0 && (
          <div className="absolute bottom-10 left-3 z-[500] pointer-events-none"
            style={{ background: 'rgba(10,15,30,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 12, padding: '8px 12px', minWidth: 140 }}>
            <p className="text-[7px] font-black text-emerald-400 uppercase tracking-[0.2em] mb-1.5">Live Coverage</p>
            <div className="space-y-1">
              <div className="flex justify-between gap-4">
                <span className="text-[9px] text-white/50">States</span>
                <span className="text-[9px] font-black text-white">{statePoints.length}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[9px] text-white/50">Cities</span>
                <span className="text-[9px] font-black text-emerald-300">{cityPoints.length}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[9px] text-white/50">Est. Reach</span>
                <span className="text-[9px] font-black text-orange-300">~{fmtReach}</span>
              </div>
            </div>
          </div>
        )}

        {/* Scan-line overlay */}
        <div className="absolute inset-0 pointer-events-none z-[400]" style={{ background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(0,0,0,0.04) 2px, rgba(0,0,0,0.04) 4px)', backgroundSize: '100% 4px' }} />
      </div>

      {/* Footer */}
      <div className="bg-[#0a0f1e] px-4 py-2 flex items-center justify-between" style={{ borderTop: '1px solid rgba(52,211,153,0.1)' }}>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-[7px] font-bold text-white/25 uppercase">
            <span className="w-2 h-2 rounded-full bg-orange-400" /> State Center
          </span>
          <span className="flex items-center gap-1 text-[7px] font-bold text-white/25 uppercase">
            <span className="w-2 h-2 rounded-full bg-emerald-400" /> City Node
          </span>
          <span className="flex items-center gap-1 text-[7px] font-bold text-white/25 uppercase">
            <span style={{ width: 14, borderTop: '1.5px dashed #34d399', display: 'inline-block' }} /> Network
          </span>
        </div>
        <span className="text-[7px] font-bold text-emerald-500/40 uppercase">IINSAF GeoNet · v2</span>
      </div>

      {/* Glowing bottom edge */}
      <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, #10b981, #34d399, #10b981, transparent)' }} />
    </div>
  );
};


const AdOverview = ({
  formData,
  totalCost,
  totalCostWithGST,
  perDayCost,
  savedAmount,
  imageSrc,
  videoSrc,
  gstRate,
  hasUserInteracted,
}) => (
  <div className="sticky top-4 p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-md sm:shadow-lg bg-white space-y-3 sm:space-y-4">
    {/* Professional Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-blue-100 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 text-blue-600"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2L15 8l6 1-4.5 4.4 1.1 6.6L12 17l-5.6 3 1.1-6.6L3 9l6-1L12 2z" />
          </svg>
        </div>
        <h2 className="text-sm sm:text-lg font-bold text-slate-800">Overview</h2>
      </div>
      <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">Live</span>
    </div>



    {/* Only showing Pricing Summary as requested */}

    {/* Compact Pricing Summary */}
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1 bg-violet-100 rounded">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-3 h-3 text-violet-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="M3 10h18M3 14h18" />
          </svg>
        </div>
        <h3 className="text-sm font-semibold text-slate-700">Pricing Summary</h3>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-slate-600">Per Day:</span>
          <span className="text-slate-800 font-medium">₹{hasUserInteracted ? perDayCost.toFixed(2) : "0.00"}</span>
        </div>

        <div className="flex justify-between text-xs">
          <span className="text-slate-600">Subtotal:</span>
          <span className="text-slate-800 font-medium">₹{hasUserInteracted ? totalCost.toFixed(2) : "0.00"}</span>
        </div>

        <div className="flex justify-between text-xs text-slate-600">
          <span>GST ({gstRate}%):</span>
          <span>₹{hasUserInteracted ? ((totalCost * gstRate) / 100).toFixed(2) : "0.00"}</span>
        </div>

        {savedAmount > 0 && (
          <div className="flex justify-between text-xs text-emerald-600">
            <span>Discount:</span>
            <span>₹{savedAmount.toFixed(2)}</span>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center rounded-lg px-3 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-bold text-sm mt-2">
        <span>Total Cost:</span>
        <span>₹{hasUserInteracted ? totalCostWithGST.toFixed(2) : "0.00"}</span>
      </div>
    </div>
  </div>
);


const PostAdvertisement = () => {

  const navigate = useNavigate();

  // State Management
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [totalCost, setTotalCost] = useState(0);
  const [totalCostWithGST, setTotalCostWithGST] = useState(0);
  const [savedAmount, setSavedAmount] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [gstRate, setGstRate] = useState(0);
  const [pricing, setPricing] = useState(null);
  const [adTypes, setAdTypes] = useState([]);
  const [channelType, setChannelType] = useState([]);
  const [plateforms, setPlateforms] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincode, setPincode] = useState([]);
  const [selectedMediaType, setSelectedMediaType] = useState("");
  const [minAdLength, setMinAdLength] = useState(0);
  const [maxAdLength, setMaxAdLength] = useState(0);
  const [minViews, setMinViews] = useState(100);
  const [maxViews, setMaxViews] = useState(10000);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const [baseView, setBaseView] = useState();
  const [pricePerCity, setPricePerCity] = useState(0);

  const [isChannelDropdownOpen, setIsChannelDropdownOpen] = useState(false);
  const [isPlateformsDropdownOpen, setIsPlateformsDropdownOpen] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isPostingAd, setIsPostingAd] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [isMobilePriceStripVisible, setIsMobilePriceStripVisible] = useState(true);
  const [step5Stage, setStep5Stage] = useState("coupon"); // coupon, overview, payment

  const [perDayCost, setPerDayCost] = useState(0);

  const [walletData, setWalletData] = useState({ balance: 0 });
  const [isMediaFullScreen, setIsMediaFullScreen] = useState(false);
  const [fullScreenMedia, setFullScreenMedia] = useState({ src: "", type: "" });

  const totalSteps = 5;

  const downloadImage = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "layout-sample.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed", error);
      toast.error("Failed to download image. Try opening in new tab.");
    }
  };


  // const [couponCode, setCouponCode] = useState("");

  useEffect(() => {
    const allStates = Object.keys(stateCityData);
    setStates(allStates);
  }, []);

  // Consolidated useEffect to close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close Channel Dropdown
      if (isChannelDropdownOpen && !event.target.closest(".channel-dropdown")) {
        setIsChannelDropdownOpen(false);
      }
      // Close Platforms Dropdown
      if (isPlateformsDropdownOpen && !event.target.closest(".platforms-dropdown")) {
        setIsPlateformsDropdownOpen(false);
      }
      // Close City Dropdown
      if (isCityDropdownOpen && !event.target.closest(".city-dropdown")) {
        setIsCityDropdownOpen(false);
      }
      // Close State Dropdown
      if (isStateDropdownOpen && !event.target.closest(".state-dropdown")) {
        setIsStateDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isChannelDropdownOpen, isPlateformsDropdownOpen, isCityDropdownOpen, isStateDropdownOpen]);

  // Form Data State
  const [formData, setFormData] = useState({
    channelType: [],
    plateforms: [],
    adType: "",
    requiredViews: "",
    perferenceArea: { pfState: "", pfStates: [], pfCities: [] },
    couponCode: "",
    baseView: baseView,
    mediaType: "image",
    mediaDescription: "",
    adLength: "5",
    userType: "", // No default selection

    image: null,
    video: null,
    startDate: new Date().toISOString().split("T")[0],
  });

  // Robust check for form incompleteness
  const isFormIncomplete = Boolean(
    !formData.adType ||
    !formData.userType ||
    !(formData.perferenceArea?.pfStates?.length > 0) ||
    !formData.perferenceArea?.pfCities?.length ||
    !formData.channelType?.length ||
    !formData.plateforms?.length ||
    !isChecked ||
    (!formData.image && !formData.video) ||
    !formData.mediaDescription?.trim() ||
    !formData.requiredViews ||
    parseInt(formData.requiredViews) < 5000
  );

  // Add state for file previews
  const [imageSrc, setImageSrc] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);

  const [preferenceCities, setPreferenceCities] = useState([]);

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Pricing data fetched:", response);

        const pricingData = response.data.data;

        setAdTypes(pricingData.adType || []);

        setChannelType(pricingData.channelType || []);
        setPlateforms(pricingData.plateforms || []);

        setPricing(pricingData);
        setMinAdLength(pricingData.minAdLength || 5);

        setMaxAdLength(Math.min(pricingData.maxAdLength || 180, 180));

        setMinViews(pricingData.minViews || 1000);

        setMaxViews(pricingData.maxViews || 100000);

        setGstRate(pricingData.gstRate);

        // Save baseView and pricePerCity to state
        setBaseView(pricingData.baseView);
        setPricePerCity(pricingData.perCityPrice);
        setFormData((prev) => ({
          ...prev,
          adLength: Math.min(parseInt(prev.adLength || 5), Math.min(pricingData.maxAdLength || 180, 180)).toString(),
          baseView: pricingData.baseView, // ✅ Add this linebaseView
        }));
      } catch (error) {
        toast.error("Failed to load pricing data. Please try again later.");

        // Set minimal default values if API fails
        setMinAdLength(5);
        setMaxAdLength(180);
        setMinViews(1000);
        setMaxViews(100000);
      }
    };

    fetchPricingData();
  }, []);

  const calculateTotalCost = useCallback(
    (discount = 0) => {
      if (pricing && pricing.adType && formData) {
        // Calculate number of days - simplified since end date is optional
        let numberOfDays = 1; // Default to 1 day (end date removed)

        // Find the selected ad type and get its price
        const selectedAdType = pricing.adType.find(
          (ad) => ad.name === formData.adType
        );
        const adTypePrice = selectedAdType ? selectedAdType.price : 0;

        // Calculate cost based on required views using ad type price
        const viewsCost = parseFloat(formData.requiredViews || 0) * adTypePrice;

        const citiesCost =
          (formData.perferenceArea?.pfCities?.length || 0) *
          (pricing.perCityPrice || 0);

        const adLengthCost =
          parseFloat(formData.adLength || 0) * (pricing.perSecPrice || 0);

        const subTotal =
          Number(viewsCost + adLengthCost + citiesCost) * numberOfDays;

        const afterGstAmount = (gstRate / 100) * subTotal;

        // Update total cost state
        setTotalCost(subTotal);

        let totalWithGST = Number(subTotal + afterGstAmount);

        // ✅ New: Calculate and set per-day cost
        const calculatedPerDayCost = Number(
          (totalWithGST / numberOfDays).toFixed(2)
        );
        setPerDayCost(calculatedPerDayCost);

        // GST + Discount

        // Apply coupon discount if there's an applied coupon
        if (appliedCoupon) {
          let discountAmount = 0;
          if (appliedCoupon.type === "percentage") {
            discountAmount = (appliedCoupon.discount / 100) * totalWithGST;
          } else if (appliedCoupon.type === "flat" || appliedCoupon.type === "fixed") {
            discountAmount = appliedCoupon.discount;
          }

          discountAmount = Math.min(discountAmount, totalWithGST);
          totalWithGST = parseFloat((totalWithGST - discountAmount).toFixed(2));
          setSavedAmount(discountAmount);
        } else {
          setSavedAmount(null);
        }

        setTotalCostWithGST(totalWithGST);
      }
    },
    [pricing, formData, gstRate, appliedCoupon]
  );

  // Effect to recalculate cost when form data changes
  useEffect(() => {
    if (pricing) {
      // If there's an applied coupon, use its discount when recalculating
      const discount = appliedCoupon ? appliedCoupon.couponDiscount : 0;
      calculateTotalCost(discount);
    }
  }, [formData, pricing, appliedCoupon, calculateTotalCost]);

  // const getCityName = (cityObj) =>
  //   typeof cityObj === "string" ? cityObj : cityObj.city;

  // Add this new handler function
  const handleChannelChange = (channelName) => {
    setFormData((prev) => ({
      ...prev,
      channelType: prev.channelType.includes(channelName)
        ? prev.channelType.filter((ch) => ch !== channelName)
        : [...prev.channelType, channelName],
    }));
  };

  // plateform change handler
  const handlePlateformChange = (plateformName) => {
    setFormData((prev) => ({
      ...prev,
      plateforms:
        prev.plateforms && prev.plateforms.includes(plateformName)
          ? prev.plateforms.filter((pf) => pf !== plateformName)
          : [...(prev.plateforms || []), plateformName],
    }));
  };

  // Event Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate start date to prevent selecting dates before today
    if (name === "startDate") {
      const today = new Date().toISOString().split("T")[0];
      if (value < today) {
        toast.error("Start date cannot be before today");
        return;
      }
    }

    // Validate end date - REMOVED (made optional)
    // if (name === "endDate") {
    //   if (value < formData.startDate) {
    //     toast.error("End date cannot be before start date");
    //     return;
    //   }
    // }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  //handle required views change
  const handleRequiredViewsChange = (e) => {
    let value = e.target.value;
    // Allow empty input for better UX
    if (value === "") {
      setFormData((prev) => ({
        ...prev,
        requiredViews: "",
      }));
      setHasUserInteracted(true);
      return;
    }
    // Only allow numbers
    value = value.replace(/\D/g, "");

    if (value && parseInt(value) > 200000) {
      toast.error("Maximum 2,00,000 views allowed");
      value = "200000";
    }

    setFormData((prev) => ({
      ...prev,
      requiredViews: value,
    }));
    setHasUserInteracted(true);
  };


  const handlePerferenceStateChange = (stateName) => {
    setFormData((prev) => {
      const cur = prev.perferenceArea.pfStates || [];
      const updated = cur.includes(stateName)
        ? cur.filter(s => s !== stateName)
        : [...cur, stateName];
      // merge cities from all selected states
      const mergedCities = updated.flatMap(s => stateCityData[s] || []);
      setPreferenceCities([...new Set(mergedCities)]);
      return {
        ...prev,
        perferenceArea: {
          ...prev.perferenceArea,
          pfStates: updated,
          pfState: updated[0] || '',   // keep backward compat for single
          pfCities: prev.perferenceArea.pfCities.filter(c => mergedCities.includes(c)),
        },
      };
    });
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    const type = formData.mediaType; // "image" or "video"

    if (!files || !files[0]) return;

    const file = files[0];
    const maxSize = type === "image" ? 15 * 1024 * 1024 : 100 * 1024 * 1024;

    if (file.size > maxSize) {
      toast.error(`File size should be less than ${maxSize / (1024 * 1024)}MB`);
      e.target.value = "";
      return;
    }

    // Revoke object URLs
    if (type === "image" && imageSrc) URL.revokeObjectURL(imageSrc);
    if (type === "video" && videoSrc) URL.revokeObjectURL(videoSrc);

    const objectUrl = URL.createObjectURL(file);

    if (type === "image") {
      setImageSrc(objectUrl);
      setFormData(prev => ({ ...prev, image: file, video: null }));
    } else {
      setVideoSrc(objectUrl);
      setFormData(prev => ({ ...prev, video: file, image: null }));
    }
  };

  const removeFile = () => {
    if (imageSrc) URL.revokeObjectURL(imageSrc);
    if (videoSrc) URL.revokeObjectURL(videoSrc);
    setImageSrc(null);
    setVideoSrc(null);
    setFormData(prev => ({ ...prev, image: null, video: null }));
    const input = document.getElementById("media-uploader");
    if (input) input.value = "";
  };

  // const handleMediaTypeChange = (e) => {
  //   setSelectedMediaType(e.target.value);
  //   setFormData((prev) => ({ ...prev, mediaType: e.target.value }));
  // };

  const handleMediaTypeChange = (e) => {
    const newType = e.target.value;

    setSelectedMediaType(newType);

    setFormData((prev) => ({
      ...prev,
      mediaType: newType,
      image: newType === "image" ? prev.image : null,
      video: newType === "video" ? prev.video : null,
    }));

    // Clear the opposite media preview
    if (newType === "image") {
      setVideoSrc(""); // clear video preview if switching to image
    } else if (newType === "video") {
      setImageSrc(""); // clear image preview if switching to video
    }
  };

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  const applyCouponCode = async (e) => {
    e.preventDefault();

    if (!formData.couponCode) {
      toast.error("Please enter a coupon code.");
      return;
    }

    const couponCode = formData.couponCode;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/apply/coupon`,
        { couponCode },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const coupon = response.data.data;

      if (!coupon || typeof coupon !== "object" || !coupon.code) {
        toast.error("Invalid or inactive coupon");
        setFormData((prev) => ({ ...prev, couponCode: "" }));
        return;
      }

      if (new Date(coupon.validUntil) < new Date()) {
        toast.error("Coupon has expired");
        setFormData((prev) => ({ ...prev, couponCode: "" }));
        return;
      }

      if (coupon.usageLimit > 0 && coupon.usedCount >= coupon.usageLimit) {
        toast.error("Coupon usage limit has been reached");
        setFormData((prev) => ({ ...prev, couponCode: "" }));
        return;
      }

      if ((totalCostWithGST ?? 0) < coupon.minPurchase) {
        toast.error(
          `Minimum purchase ₹${coupon.minPurchase} required to use this coupon`
        );
        setFormData((prev) => ({ ...prev, couponCode: "" }));
        return;
      }

      // Set the applied coupon - calculateTotalCost will handle the discount calculation
      setAppliedCoupon(coupon);
      toast.success("Coupon applied successfully");
    } catch (error) {
      console.error("Error applying coupon:", error);
      toast.error(" applied coupon is not valid.");
      setFormData((prev) => ({ ...prev, couponCode: "" }));
    }
  };

  // Step Validation and Navigation
  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.channelType || formData.channelType.length === 0) {
          toast.error("Please select at least one channel type");
          return false;
        }
        if (!formData.userType) {
          toast.error("Please select a target user type");
          return false;
        }
        if (!formData.plateforms || formData.plateforms.length === 0) {
          toast.error("Please select at least one platform");
          return false;
        }
        return true;
      case 2:
        if (!formData.startDate) {
          toast.error("Please select a start date");
          return false;
        }
        if (!formData.requiredViews || parseInt(formData.requiredViews) < 5000) {
          toast.error("Required views must be at least 5000");
          return false;
        }
        if (parseInt(formData.requiredViews) > 200000) {
          toast.error("Required views cannot exceed 200,000 (2 Lakh)");
          return false;
        }
        return true;
      case 3:
        if (!formData.perferenceArea.pfState) {
          toast.error("Please select a target state");
          return false;
        }
        if (!formData.perferenceArea.pfCities || formData.perferenceArea.pfCities.length === 0) {
          toast.error("Please select at least one city");
          return false;
        }
        return true;
      case 4:
        if (!formData.adType) {
          toast.error("Please select an ad type");
          return false;
        }
        if (formData.mediaType === "image" && !formData.image) {
          toast.error("Please upload an image");
          return false;
        }
        if (formData.mediaType === "video" && !formData.video) {
          toast.error("Please upload a video");
          return false;
        }
        if (!formData.mediaDescription?.trim()) {
          toast.error("Please provide a media description");
          return false;
        }
        return true;
      case 5:
        if (!isChecked) {
          toast.error("Please accept the terms and conditions");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Function to validate form before showing confirmation
  const validateForm = (e) => {
    e.preventDefault();

    // Debug: Log current formData
    console.log("🔍 Debug - Current formData:", formData);

    // Check if mediaType is selected
    if (!formData.mediaType) {
      toast.error("Please select a media type");
      return false;
    }

    // Normalize adType for validation
    const normalizedAdType = formData.adType
      ? formData.adType.trim().toLowerCase()
      : "";

    // Validate upload based on adType and mediaType
    if (normalizedAdType === "video" && !formData.video) {
      toast.error("Please upload a video for a video ad.");
      return false;
    }

    if (normalizedAdType !== "video" && !formData.image) {
      toast.error("Please upload an image for a non-video ad.");
      return false;
    }

    // Check terms and conditions
    if (!isChecked) {
      toast.error("Please accept terms and conditions");
      return false;
    }

    // Check channel type
    if (!formData.channelType || formData.channelType.length === 0) {
      toast.error("Please select at least one channel");
      return false;
    }

    // Check user type
    if (!formData.userType) {
      toast.error("Please select target user type (Reporter or Influencer)");
      return false;
    }

    // Check ad type
    if (!formData.adType) {
      toast.error("Please select an ad type");
      return false;
    }

    // Check start date
    if (!formData.startDate) {
      toast.error("Please select a start date");
      return false;
    }

    // Check required views
    if (!formData.requiredViews || parseInt(formData.requiredViews) < 5000) {
      toast.error("Required views must be between 5,000 and 2,00,000");
      return false;
    }
    if (parseInt(formData.requiredViews) > 200000) {
      toast.error("Maximum 2,00,000 views allowed");
      return false;
    }

    // Check media description
    if (!formData.mediaDescription?.trim()) {
      toast.error("Please specify media description");
      return false;
    }

    // Check ad length
    if (!formData.adLength) {
      toast.error("Please specify ad length");
      return false;
    }

    // Check platforms
    if (!formData.plateforms || formData.plateforms.length === 0) {
      toast.error("Please select at least one platform");
      return false;
    }

    // Check preference state
    if (!formData.perferenceArea?.pfState?.trim()) {
      toast.error("Please select a target State");
      return false;
    }

    // Check preference cities
    if (!formData.perferenceArea?.pfCities?.length) {
      toast.error("Please select at least one City");
      return false;
    }

    // Double-check media type upload
    if (formData.mediaType === "image" && !formData.image) {
      toast.error("Please upload an image");
      return false;
    }

    if (formData.mediaType === "video" && !formData.video) {
      toast.error("Please upload a video");
      return false;
    }

    // Final confirmation
    fetchFinalPrice(); // get calculated price
    setShowConfirmModal(true); // show confirmation before submission
    return true;
  };


  // Function to fetch final price from backend
  const fetchFinalPrice = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/get/final/price`,
        {
          adType: formData.adType,
          adLength: formData.adLength,
          CouponCode: formData.couponCode,
          requiredViews: formData.requiredViews,
          cities: formData.perferenceArea.pfCities.length,
          // requiredReporters: requiredReporter,
          days: 1, // End date removed - always use 1 day
          mediaType: formData.mediaType,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data && response.data.success) {
        setTotalCost(response.data.data.totalBaseCost);
        setTotalCostWithGST(response.data.data.finalPrice);
      }
    } catch (error) {
      toast.error(
        "Failed to fetch final price. Using calculated price instead."
      );
    }
  };

  // Function to handle wallet payment
  const handleWalletPayment = async () => {
    try {
      setIsSubmitting(true);
      setIsPostingAd(true);

      // Send only the amount to backend for wallet payment
      const walletPaymentData = {
        amount: totalCostWithGST,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/ads/wallet/payment`,
        walletPaymentData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Payment successful! Processing your advertisement...");

        // After successful wallet payment, proceed with ad submission
        await submitAdvertisement();
      }
    } catch (error) {
      console.error("Wallet payment error:", error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Wallet payment failed. Please try again.");
      }
      setIsPostingAd(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to submit advertisement (extracted from handleSubmit)
  const submitAdvertisement = async () => {
    try {
      setIsPostingAd(true);
      setUploadProgress(0);
      const adData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key === "image" || key === "video") {
          if (formData[key]) adData.append(key, formData[key]);
        } else if (key === "perferenceArea") {
          adData.append("pfState", formData.perferenceArea.pfState);
          formData.perferenceArea.pfCities.forEach((city, i) => {
            adData.append("pfCities[]", city);
          });
        } else if (
          key === "channelType" &&
          Array.isArray(formData.channelType)
        ) {
          formData.channelType.forEach((channel, index) => {
            adData.append(`channelType[${index}]`, channel);
          });
        } else if (
          key === "plateforms" &&
          Array.isArray(formData.plateforms)
        ) {
          formData.plateforms.forEach((platform, index) => {
            adData.append(`platforms[${index}]`, platform);
          });
        } else if (key !== "userType") {
          adData.append(key, formData[key]);
        }
      });

      adData.append("subtotal", totalCost);
      adData.append("gst", (totalCost * gstRate) / 100);
      adData.append("totalCost", totalCostWithGST);
      adData.append("userType", formData.userType);

      // Debug: Log the userType being sent
      console.log("🔍 Debug - userType being sent:", formData.userType, "Type:", typeof formData.userType);

      if (appliedCoupon) {
        const cleanedCouponCode = Array.isArray(appliedCoupon.couponCode)
          ? appliedCoupon.couponCode.find((c) => c && c !== "undefined")
          : appliedCoupon.couponCode !== "undefined"
            ? appliedCoupon.couponCode
            : null;

        if (cleanedCouponCode) {
          adData.append("couponCode", cleanedCouponCode);
        }

        const cleanedDiscount =
          appliedCoupon.couponDiscount !== "undefined"
            ? appliedCoupon.couponDiscount
            : null;

        if (cleanedDiscount) {
          adData.append("couponDiscount", cleanedDiscount);
        }

        if (savedAmount) {
          adData.append("savedAmount", savedAmount);
        }
      }

      const baseViews = baseView;
      const requiredReporter = Math.floor(
        Number(formData.requiredViews) / baseViews
      );
      adData.append("requiredReporter", requiredReporter);

      const adResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/advertiser/post/advertisement`,
        adData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      let ownerId = adResponse.data.owner;

      if (adResponse.status === 201) {
        setIsPostingAd(false);
        localStorage.setItem("ownerId", ownerId);
        toast.success("Advertisement submitted successfully!");

        navigate("/advertiser/leads-status/underReview", {
          state: { ownerId: ownerId },
        });
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          "Failed to post advertisement. Please try again later."
        );
      }
      setIsPostingAd(false);
      console.error("Ad post failed:", error);
    }
  };

  // Function to handle the actual submission after confirmation
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const paymentAmount = Math.round(totalCostWithGST * 100);

      const paymentResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/ads/payment`,
        {
          amount: paymentAmount,
          currency: "INR",
          // adID: formData.adID
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Payment response:", paymentResponse.data);

      const { order_id, amount, currency } = paymentResponse.data;

      const options = {
        key: paymentResponse.data.key || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount,
        currency,
        order_id,
        name: "Ad Submission Payment",
        description: "Payment to post your ad",
        handler: async function (response) {
          const { razorpay_payment_id } = response;

          try {
            const verifyRes = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/payment/${razorpay_payment_id}`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );
            console.log("Payment verification response:", verifyRes.data);
            const status = verifyRes.data.status;

            if (status === "captured") {
              // Show loader while posting ad
              setIsPostingAd(true);
              setUploadProgress(0);

              const adData = new FormData();

              Object.keys(formData).forEach((key) => {
                if (key === "image" || key === "video") {
                  if (formData[key]) adData.append(key, formData[key]);
                } else if (key === "perferenceArea") {
                  // append the state
                  adData.append("pfState", formData.perferenceArea.pfState);
                  // append each selected city
                  formData.perferenceArea.pfCities.forEach((city, i) => {
                    // use pfCities[] if your backend expects an array
                    adData.append("pfCities[]", city);
                    //—or if it expects indexed keys:
                    // adData.append(`pfCities[${i}]`, city);
                  });
                } else if (
                  key === "channelType" &&
                  Array.isArray(formData.channelType)
                ) {
                  formData.channelType.forEach((channel, index) => {
                    adData.append(`channelType[${index}]`, channel);
                    //—or use channelType[] as above
                    // adData.append('channelType[]', channel);
                  });
                } else if (
                  key === "plateforms" &&
                  Array.isArray(formData.plateforms)
                ) {
                  formData.plateforms.forEach((platform, index) => {
                    adData.append(`platforms[${index}]`, platform);
                  });
                } else if (key !== "userType") {
                  adData.append(key, formData[key]);
                }
              });

              // then your subtotal/gst/totalCost/coupon stuff…
              adData.append("subtotal", totalCost);
              adData.append("gst", (totalCost * gstRate) / 100);
              adData.append("totalCost", totalCostWithGST);
              adData.append("userType", formData.userType);

              // Debug: Log the userType being sent
              console.log("🔍 Debug - userType being sent (Razorpay):", formData.userType, "Type:", typeof formData.userType);

              if (appliedCoupon) {
                const cleanedCouponCode = Array.isArray(
                  appliedCoupon.couponCode
                )
                  ? appliedCoupon.couponCode.find((c) => c && c !== "undefined")
                  : appliedCoupon.couponCode !== "undefined"
                    ? appliedCoupon.couponCode
                    : null;

                if (cleanedCouponCode) {
                  adData.append("couponCode", cleanedCouponCode);
                }

                const cleanedDiscount =
                  appliedCoupon.couponDiscount !== "undefined"
                    ? appliedCoupon.couponDiscount
                    : null;

                if (cleanedDiscount) {
                  adData.append("couponDiscount", cleanedDiscount);
                }

                if (savedAmount) {
                  adData.append("savedAmount", savedAmount);
                }
              }

              // Calculate requiredReporter
              const baseViews = baseView; // or get from API/pricing if dynamic

              const requiredReporter = Math.floor(
                Number(formData.requiredViews) / baseViews
              );

              // When creating adData for submission:
              adData.append("requiredReporter", requiredReporter);

              try {
                const adResponse = await axios.post(
                  `${process.env.REACT_APP_API_BASE_URL}/advertiser/post/advertisement`,
                  adData,
                  {
                    headers: {
                      "Content-Type": "multipart/form-data",
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    onUploadProgress: (progressEvent) => {
                      const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                      );
                      setUploadProgress(percentCompleted);
                    },
                  }
                );

                let ownerId = adResponse.data.owner;

                if (adResponse.status === 201) {
                  setIsPostingAd(false);
                  localStorage.setItem("ownerId", ownerId);
                  toast.success("Advertisement submitted successfully!");

                  navigate("/advertiser/leads-status/underReview", {
                    state: { ownerId: ownerId },
                  });
                }

                // processSuccessfulSubmission(adResponse.data);
              } catch (error) {
                // Backend validation or server error
                if (
                  error.response &&
                  error.response.data &&
                  error.response.data.message
                ) {
                  toast.error(error.response.data.message);
                } else {
                  toast.error(
                    "Failed to post advertisement. Please try again later."
                  );
                  setIsPostingAd(false);
                }

                console.error("Ad post failed:", error);
              }
            } else {
              toast.error("Payment was not successful. Please try again.");
            }
          } catch (verifyError) {
            console.error("Error verifying payment:", verifyError);
            toast.error("Error verifying payment. Please try again.");
          }
        },
        prefill: {
          name: "Rohit Sharma",
          email: "rohit@example.com",
          contact: "9999999999",
        },
        theme: {
          color: "#3399cc",
        },
      };

      if (!window.Razorpay) {
        toast.error("Razorpay SDK not loaded. Please check your connection.");
        return;
      }

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Error during payment/ad posting", error);
      toast.error("Something went wrong during payment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add this handler function with your other handlers

  // Add this function to handle city selection
  const handleCityChange = (city) => {
    setFormData((prev) => {
      const currentCities = prev.perferenceArea.pfCities || []; // ✅ Correct reference
      const newCities = currentCities.includes(city)
        ? currentCities.filter((c) => c !== city) // Remove city
        : [...currentCities, city]; // Add city

      return {
        ...prev,
        perferenceArea: {
          ...prev.perferenceArea,
          pfCities: newCities, // ✅ Update correctly
        },
      };
    });
  };

  const handleSelectAllCities = (e) => {
    const isChecked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      perferenceArea: {
        ...prev.perferenceArea,
        pfCities: isChecked ? preferenceCities : [], // ✅ Select all or deselect all
      },
    }));
  };

  useEffect(() => {
    const fetchWalletBalance = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/wallet/detail`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Wallet data fetched:", response);
        setWalletData({
          balance: response.data.balance || 0,
        });
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
        setWalletData({ balance: 0 });
      }
    };

    fetchWalletBalance();
  }, []);

  // Full-Screen Lightbox Component
  const MediaLightbox = () => {
    if (!isMediaFullScreen) return null;
    return (
      <div 
        className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-10 animate-in fade-in duration-300"
        onClick={() => setIsMediaFullScreen(false)}
      >
        <button 
          className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all z-20"
          onClick={() => setIsMediaFullScreen(false)}
        >
          <span className="text-2xl font-black">✕</span>
        </button>
        
        <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          {fullScreenMedia.type === "image" ? (
            <img 
              src={fullScreenMedia.src} 
              alt="Full Preview" 
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-500" 
            />
          ) : (
            <video 
              src={fullScreenMedia.src} 
              controls 
              autoPlay
              className="max-w-full max-h-full rounded-lg shadow-2xl animate-in zoom-in-95 duration-500" 
            />
          )}
          
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-2xl text-center">
            <p className="text-white text-sm font-bold truncate max-w-[200px] sm:max-w-md">
              {formData.adType || "Advertisement Preview"}
            </p>
            <p className="text-white/60 text-[10px] uppercase font-black tracking-widest mt-1">Full Size Preview</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-3 sm:p-6 lg:p-8">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg sm:text-3xl font-black text-slate-800 tracking-tight">
            Create <span className="text-blue-600">Campaign</span>
          </h1>
          <p className="text-slate-500 text-[11px] sm:text-sm font-medium mt-0.5">Fill in the details to reach your audience</p>
        </div>
        <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl shadow-sm border border-slate-100">
          <div className="flex -space-x-1.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className={`w-1.5 h-1.5 rounded-full border border-white ${currentStep >= s ? "bg-blue-600" : "bg-slate-200"}`} />
            ))}
          </div>
          <span className="text-[10px] font-bold text-slate-600">Step {currentStep}/{totalSteps}</span>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="max-w-4xl mx-auto mb-3 sm:mb-8">
        <div className="flex items-center justify-between relative px-1">
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-200 -translate-y-1/2 z-0"></div>
          <div
            className="absolute top-1/2 left-0 h-0.5 bg-blue-600 -translate-y-1/2 z-0 transition-all duration-300"
            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
          ></div>

          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className="relative z-10 flex flex-col items-center">
              <div
                className={`w-6 h-6 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-[10px] sm:text-base transition-all duration-500 ${currentStep === step
                  ? "bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)] scale-110"
                  : currentStep > step
                    ? "bg-emerald-500 text-white"
                    : "bg-white text-slate-400 border-2 border-slate-100"
                  }`}
              >
                {currentStep > step ? <FaCheckCircle className="w-3 h-3 sm:w-5 sm:h-5" /> : step}
              </div>
              <span className={`text-[9px] sm:text-xs mt-1 font-bold tracking-tight uppercase whitespace-nowrap transition-colors duration-300 ${currentStep >= step ? "text-blue-600" : "text-slate-400"}`}>
                {step === 1 ? "Platform Select " : step === 2 ? "Goals" : step === 3 ? "Target Audience" : step === 4 ? "Creative Media" : "Finalize"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6 mb-20">
        <div className="lg:col-span-2">
          <form onSubmit={validateForm} className="space-y-3 sm:space-y-6">

            {currentStep === 1 && (
              <div className="rounded-xl sm:rounded-3xl shadow-lg bg-white border border-slate-100 max-w-4xl mx-auto mb-3 animate-in fade-in slide-in-from-bottom-4 duration-500 relative z-10">
                {/* Header with inline Next button */}
                <div className="px-3 py-2.5 sm:px-8 sm:py-6 bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-between rounded-t-xl sm:rounded-t-[2rem]">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-white/10 rounded-lg text-white border border-white/20">
                      <FaInfoCircle className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <h2 className="text-sm font-black text-white tracking-tight leading-tight">Campaign Basics</h2>
                      <p className="text-white/50 text-[8px] uppercase font-bold tracking-widest">Step 1 of 5</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-bold rounded-lg border border-white/20 transition-all"
                  >
                    Next <FaArrowRight className="w-2.5 h-2.5" />
                  </button>
                </div>
                <div className="p-3 sm:p-10 space-y-4 sm:space-y-12">
                  <div className="grid grid-cols-2 gap-3 sm:gap-10">
                    {/* Channel Type */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                        Channel Type <span className="text-red-500">*</span>
                      </label>
                      <div className="relative channel-dropdown">
                        <button
                          type="button"
                          onClick={() => setIsChannelDropdownOpen(!isChannelDropdownOpen)}
                          className={`w-full px-3 py-2 bg-white border ${formData.channelType.length === 0 ? "border-red-300" : "border-gray-200"} rounded-lg flex justify-between items-center text-xs shadow-sm focus:ring-1 focus:ring-blue-400 transition-all`}
                        >
                          <span className="text-xs text-gray-600 truncate">
                            {formData.channelType.length > 0 ? `${formData.channelType.length} selected` : "Select"}
                          </span>
                          <FaChevronDown className={`w-2.5 h-2.5 transition-transform flex-shrink-0 ml-1 ${isChannelDropdownOpen ? "rotate-180" : ""} text-gray-500`} />
                        </button>
                        {isChannelDropdownOpen && (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-auto">
                            {channelType.map((channel, index) => (
                              <label key={index} className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-0 border-gray-50">
                                <input type="checkbox" checked={formData.channelType.includes(channel.name)} onChange={() => handleChannelChange(channel.name)} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                                <span className="ml-2 text-xs text-gray-700 font-medium">{channel.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      {formData.channelType.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {formData.channelType.map((channel) => (
                            <div key={channel} className="flex items-center bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-[9px] font-bold border border-blue-100">
                              {channel}
                              <button type="button" onClick={() => handleChannelChange(channel)} className="ml-1 hover:text-red-500 font-black">✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Platforms */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                        Platforms <span className="text-red-500">*</span>
                      </label>
                      <div className="relative platforms-dropdown">
                        <button
                          type="button"
                          onClick={() => setIsPlateformsDropdownOpen(!isPlateformsDropdownOpen)}
                          className={`w-full px-3 py-2 bg-white border ${formData.plateforms.length === 0 ? "border-red-300" : "border-gray-200"} rounded-lg flex justify-between items-center text-xs shadow-sm focus:ring-1 focus:ring-blue-400 transition-all`}
                        >
                          <span className="text-xs text-gray-600 truncate">
                            {formData.plateforms.length > 0 ? `${formData.plateforms.length} selected` : "Select"}
                          </span>
                          <FaChevronDown className={`w-2.5 h-2.5 transition-transform flex-shrink-0 ml-1 ${isPlateformsDropdownOpen ? "rotate-180" : ""} text-gray-500`} />
                        </button>
                        {isPlateformsDropdownOpen && (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-auto">
                            {plateforms.map((plateform, index) => (
                              <label key={index} className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-0 border-gray-50">
                                <input type="checkbox" checked={formData.plateforms.includes(plateform)} onChange={() => handlePlateformChange(plateform)} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                                <span className="ml-2 text-xs text-gray-700 font-medium">{plateform}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      {formData.plateforms.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {formData.plateforms.map((platform) => (
                            <div key={platform} className="flex items-center bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-[9px] font-bold border border-indigo-100">
                              {platform}
                              <button type="button" onClick={() => handlePlateformChange(platform)} className="ml-1 hover:text-red-500 font-black">✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* User Type Selection */}
                  <div className="space-y-2">
                    <p className="text-xs font-black text-slate-600 tracking-tight">Target Audience <span className="text-red-500">*</span></p>
                    <select
                      name="userType"
                      value={formData.userType}
                      onChange={(e) => setFormData((prev) => ({ ...prev, userType: e.target.value }))}
                      className={`w-full px-3 py-2 bg-white border ${!formData.userType ? "border-red-300" : "border-gray-200"} rounded-lg text-xs font-bold shadow-sm focus:ring-1 focus:ring-blue-400 outline-none transition-all cursor-pointer`}
                    >
                      <option value="">Choose Audience</option>
                      <option value="reporter">Reporters</option>
                      <option value="influencer">Influencers</option>
                    </select>
                    {formData.userType && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center bg-blue-50 text-blue-700 px-2 py-0.5 rounded-sm text-[9px] font-bold border border-blue-100 uppercase tracking-tighter w-fit">
                          SELECTED: {formData.userType === "reporter" ? "Reporters" : "Influencers"}
                        </div>
                        <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-2.5">
                           <div className="mt-1 w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
                           <p className="text-[10px] font-black text-slate-500 leading-tight">
                              NO {formData.userType === "reporter" ? "REPORTER" : "INFLUENCER"} REPORT WILL SHARE THIS REACH DATA. (INTERNAL ESTIMATE ONLY)
                           </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* MOBILE VIEW ONLY: Video Guide */}
                  <div className="block lg:hidden mt-6 pt-6 border-t border-slate-100">
                    <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-4 shadow-sm relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-red-500/5 rounded-full blur-xl"></div>
                      
                      <div className="flex items-center gap-2.5 mb-3">
                        <div className="p-2 bg-red-50 text-red-500 rounded-xl border border-red-100 flex items-center justify-center">
                          <FaPlayCircle className="w-4 h-4 animate-pulse" />
                        </div>
                        <div>
                          <h3 className="text-xs font-black text-slate-800 tracking-tight leading-tight">How to Use</h3>
                          <p className="text-slate-400 text-[8px] uppercase font-bold tracking-widest">Video Guide</p>
                        </div>
                      </div>

                      {/* Video Embed Container */}
                      <div className="relative aspect-[9/16] max-h-[300px] mx-auto rounded-xl overflow-hidden border border-slate-200 shadow-inner bg-slate-950">
                        <iframe
                          className="absolute inset-0 w-full h-full"
                          src="https://www.youtube.com/embed/uookHpnxyrY"
                          title="How to use video guide mobile"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        ></iframe>
                      </div>
                      
                      <div className="mt-2.5 text-center">
                        <a
                          href="https://www.youtube.com/shorts/uookHpnxyrY"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-red-600 font-bold hover:underline"
                        >
                          Watch on YouTube <FaArrowRight className="w-2 h-2" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="rounded-xl sm:rounded-3xl shadow-lg bg-white border border-slate-100 max-w-4xl mx-auto mb-3 animate-in fade-in slide-in-from-bottom-4 duration-500 relative z-10">
                <div className="px-3 py-2.5 sm:px-8 sm:py-6 bg-gradient-to-br from-blue-700 to-indigo-800 flex items-center justify-between rounded-t-xl sm:rounded-t-[2rem]">
                  <button type="button" onClick={prevStep} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-lg border border-white/20 transition-all">
                    <FaArrowLeft className="w-2.5 h-2.5" /> Back
                  </button>
                  <div className="text-center">
                    <h2 className="text-xs font-black text-white tracking-tight">Campaign Goals</h2>
                    <p className="text-white/50 text-[8px] uppercase font-bold">Step 2 of 5</p>
                  </div>
                  <button type="button" onClick={nextStep} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-bold rounded-lg border border-white/20 transition-all">
                    Next <FaArrowRight className="w-2.5 h-2.5" />
                  </button>
                </div>
                <div className="p-3 sm:p-10 space-y-3 sm:space-y-12">
                  <div className="grid grid-cols-2 gap-3 sm:gap-10">
                    {/* Start Date */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Start Date <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={handleChange}
                        className="w-full px-2.5 py-2 text-xs bg-white border border-gray-200 rounded-lg shadow-sm focus:ring-1 focus:ring-blue-400 outline-none transition-all"
                      />
                    </div>

                    {/* Required Views */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Required Views <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        name="requiredViews"
                        value={formData.requiredViews}
                        onChange={handleRequiredViewsChange}
                        placeholder="5,000–2,00,000"
                        className={`w-full px-2.5 py-2 text-xs bg-white border ${!formData.requiredViews || parseInt(formData.requiredViews) < 5000 || parseInt(formData.requiredViews) > 200000 ? "border-red-300" : "border-gray-200"} rounded-lg shadow-sm focus:ring-1 focus:ring-blue-400 outline-none transition-all`}
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-700 via-indigo-600 to-blue-800 rounded-xl p-4 text-white relative overflow-hidden shadow-inner group">
                    {/* Animated scanning light */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer pointer-events-none"></div>
                    
                    <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-2">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                           <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                             <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-300 opacity-75"></span>
                             <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-blue-100"></span>
                           </span>
                           <p className="text-[8px] sm:text-[10px] uppercase font-black tracking-widest text-blue-100/80">Estimated Campaign Reach</p>
                        </div>
                        <div className="text-lg sm:text-2xl font-black tracking-tighter flex flex-wrap items-baseline gap-1.5 sm:gap-2 drop-shadow-md">
                          {formData.requiredViews ? parseInt(formData.requiredViews).toLocaleString() : "0"} 
                          <span className="text-xs sm:text-sm font-bold opacity-60 uppercase">to</span> 
                          {formData.requiredViews ? (parseInt(formData.requiredViews) * 12).toLocaleString() : "0"}
                          <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider opacity-80">Impressions</span>
                        </div>
                      </div>
                      
                      {/* NEW: Dynamic Channel/Reporter Counter - Optimized for Mobile */}
                      <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2">
                         <div className="bg-white/10 backdrop-blur-md rounded-lg px-2 py-1 sm:px-3 sm:py-1.5 border border-white/20 shadow-lg text-right group-hover:bg-white/20 transition-all flex flex-col items-center sm:items-end">
                            <div className="text-[7px] sm:text-[8px] uppercase font-black text-blue-200 leading-none mb-1">Reporter Network</div>
                            <div className="text-sm sm:text-lg font-black leading-none flex items-center justify-end gap-1 sm:gap-1.5">
                               <FaUsers className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-200" />
                               {formData.requiredViews ? Math.floor(parseInt(formData.requiredViews) / 3000) || 1 : "0"}
                               <span className="text-[9px] sm:text-[10px] font-bold opacity-60">~</span>
                               {formData.requiredViews ? Math.ceil(parseInt(formData.requiredViews) / 1800) || 1 : "0"}
                            </div>
                         </div>
                         <div className="flex gap-0.5 px-1 order-first sm:order-last">
                            {[...Array(5)].map((_, i) => (
                              <div key={i} className={`w-0.5 h-2.5 sm:h-3 rounded-full ${i < 4 ? "bg-white" : "bg-white/30"} animate-pulse`} style={{ animationDelay: `${i * 0.2}s` }}></div>
                            ))}
                         </div>
                      </div>
                    </div>

                    {/* Reach Progress Meter */}
                    <div className="mt-3 h-1.5 bg-black/20 rounded-full overflow-hidden border border-white/10">
                       <div 
                         className="h-full bg-gradient-to-r from-blue-300 to-white transition-all duration-1000 ease-out rounded-full shadow-[0_0_10px_rgba(255,255,255,0.5)]"
                         style={{ width: formData.requiredViews ? `${Math.min((parseInt(formData.requiredViews) / 200000) * 100, 100)}%` : '0%' }}
                       ></div>
                    </div>
                    
                    <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-400/20 rounded-full blur-3xl group-hover:bg-blue-300/30 transition-all duration-700"></div>
                  </div>

                  {/* Reach Privacy Disclaimer */}
                  <div className="flex items-center gap-2 px-2 opacity-60">
                    <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
                    <p className="text-[10px] font-medium text-slate-500 italic">Note: These are internal performance estimates; no reports will share this estimated reach with third parties.</p>
                  </div>

                  {/* Ad Length */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-semibold text-slate-600">Ad Duration <span className="text-red-500">*</span></label>
                      <span className="px-2.5 py-0.5 bg-blue-600 text-white text-[10px] font-black rounded-full">{formData.adLength}s</span>
                    </div>
                    <input
                      type="range"
                      name="adLength"
                      value={formData.adLength}
                      onChange={handleChange}
                      min={minAdLength}
                      max={Math.min(maxAdLength, 180)}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-[9px] font-bold text-gray-400 uppercase">
                      <span>{minAdLength}s Min</span>
                      <span>{Math.min(maxAdLength, 180)}s Max</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="rounded-xl sm:rounded-3xl shadow-lg bg-white border border-slate-100 max-w-4xl mx-auto mb-3 animate-in fade-in slide-in-from-bottom-4 duration-500 relative z-10">
                <div className="px-3 py-2.5 sm:px-8 sm:py-6 bg-gradient-to-br from-emerald-600 to-teal-800 flex items-center justify-between rounded-t-xl sm:rounded-t-[2rem]">
                  <button type="button" onClick={prevStep} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-lg border border-white/20 transition-all">
                    <FaArrowLeft className="w-2.5 h-2.5" /> Back
                  </button>
                  <div className="text-center">
                    <h2 className="text-xs font-black text-white tracking-tight">Location Targeting</h2>
                    <p className="text-white/50 text-[8px] uppercase font-bold">Step 3 of 5</p>
                  </div>
                  <button type="button" onClick={nextStep} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-bold rounded-lg border border-white/20 transition-all">
                    Next <FaArrowRight className="w-2.5 h-2.5" />
                  </button>
                </div>
                <div className="p-3 sm:p-10 space-y-3 sm:space-y-12">
                  <div className="grid grid-cols-1 gap-3 sm:gap-10 relative" style={{ zIndex: 1200 }}>
                    {/* State multi-select Dropdown */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Preference State <span className="text-red-500">*</span></label>
                      <div className="relative state-dropdown">
                        <button
                          type="button"
                          onClick={() => setIsStateDropdownOpen(prev => !prev)}
                          className={`w-full px-2.5 py-2 bg-white border ${!formData.perferenceArea.pfStates?.length ? 'border-red-300' : 'border-gray-200'
                            } rounded-lg flex justify-between items-center text-xs shadow-sm focus:ring-1 focus:ring-emerald-400 transition-all`}
                        >
                          <span className="text-xs text-gray-600">
                            {formData.perferenceArea.pfStates?.length > 0
                              ? `${formData.perferenceArea.pfStates.length} state${formData.perferenceArea.pfStates.length > 1 ? 's' : ''} selected`
                              : 'Select State(s)'}
                          </span>
                          <FaChevronDown className={`w-2.5 h-2.5 transition-transform ${isStateDropdownOpen ? 'rotate-180' : ''} text-gray-500`} />
                        </button>
                        {isStateDropdownOpen && (
                          <div className="absolute z-[1100] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-auto">
                            {states.map((state) => (
                              <label key={state} className="flex items-center px-3 py-2 hover:bg-emerald-50 cursor-pointer border-b last:border-0 border-gray-50">
                                <input
                                  type="checkbox"
                                  checked={formData.perferenceArea.pfStates?.includes(state) || false}
                                  onChange={() => handlePerferenceStateChange(state)}
                                  className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-xs text-gray-700 font-medium">{state}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      {formData.perferenceArea.pfStates?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {formData.perferenceArea.pfStates.map(state => (
                            <div key={state} className="flex items-center bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-bold border border-emerald-100">
                              {state}
                              <button type="button" onClick={() => handlePerferenceStateChange(state)} className="ml-1 hover:text-red-500 font-black">✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Cities Dropdown */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Preference Cities <span className="text-red-500">*</span></label>
                      <div className="relative city-dropdown">
                        <button
                          type="button"
                          onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
                          className={`w-full px-2.5 py-2 bg-white border ${(!formData.perferenceArea.pfCities || formData.perferenceArea.pfCities.length === 0) ? "border-red-300" : "border-gray-200"} rounded-lg flex justify-between items-center text-xs shadow-sm focus:ring-1 focus:ring-emerald-400 transition-all`}
                          disabled={!formData.perferenceArea.pfState}
                        >
                          <span className="text-xs text-gray-600">
                            {formData.perferenceArea.pfCities?.length > 0 ? `${formData.perferenceArea.pfCities.length} cities selected` : "Select cities"}
                          </span>
                          <FaChevronDown className={`w-2.5 h-2.5 transition-transform ${isCityDropdownOpen ? "rotate-180" : ""} text-gray-500`} />
                        </button>
                        {isCityDropdownOpen && (
                          <div className="absolute z-[1100] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-auto">
                            <div className="flex bg-gray-50 border-b p-1.5 gap-2">
                              <button type="button" onClick={() => setFormData(p => ({ ...p, perferenceArea: { ...p.perferenceArea, pfCities: preferenceCities } }))} className="flex-1 py-1 text-xs font-bold bg-emerald-50 text-emerald-600 rounded-md hover:bg-emerald-100">All</button>
                              <button type="button" onClick={() => setFormData(p => ({ ...p, perferenceArea: { ...p.perferenceArea, pfCities: [] } }))} className="flex-1 py-1 text-xs font-bold bg-red-50 text-red-600 rounded-md hover:bg-red-100">Clear</button>
                            </div>
                            {preferenceCities.map((city) => (
                              <label key={city} className="flex items-center px-3 py-2 hover:bg-emerald-50 cursor-pointer border-b border-gray-50 last:border-0">
                                <input type="checkbox" checked={formData.perferenceArea.pfCities?.includes(city)} onChange={() => handleCityChange(city)} className="h-4 w-4 text-emerald-600 border-gray-300 rounded" />
                                <span className="ml-2 text-xs text-gray-700">{city}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      {formData.perferenceArea.pfCities?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {formData.perferenceArea.pfCities.map((city) => (
                            <div key={city} className="flex items-center bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-bold border border-emerald-100">
                              {city}
                              <button type="button" onClick={() => handleCityChange(city)} className="ml-1 hover:text-red-500 font-black">✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Interactive India Map Visualization */}
                  <IndiaMap
                    selectedStates={formData.perferenceArea.pfStates || (formData.perferenceArea.pfState ? [formData.perferenceArea.pfState] : [])}
                    selectedCities={formData.perferenceArea.pfCities}
                  />
                </div>
              </div>
            )}


            {currentStep === 4 && (
              <div className="rounded-xl sm:rounded-3xl shadow-lg bg-white border border-slate-100 max-w-4xl mx-auto mb-3 animate-in fade-in slide-in-from-bottom-4 duration-500 relative z-10">
                <div className="px-3 py-2.5 sm:px-8 sm:py-6 bg-gradient-to-br from-orange-700 to-amber-800 flex items-center justify-between rounded-t-xl sm:rounded-t-[2rem]">
                  <button type="button" onClick={prevStep} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-lg border border-white/20 transition-all">
                    <FaArrowLeft className="w-2.5 h-2.5" /> Back
                  </button>
                  <div className="text-center">
                    <h2 className="text-xs font-black text-white tracking-tight">Ad Creative</h2>
                    <p className="text-white/50 text-[8px] uppercase font-bold">Step 4 of 5</p>
                  </div>
                  <button type="button" onClick={nextStep} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-bold rounded-lg border border-white/20 transition-all">
                    Next <FaArrowRight className="w-2.5 h-2.5" />
                  </button>
                </div>
                <div className="p-3 sm:p-10 space-y-4 sm:space-y-12">
                  <div className="space-y-4">
                    {/* Ad Type */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">Format <span className="text-red-500">*</span></label>
                      <select
                        name="adType"
                        value={formData.adType}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            adType: val,
                            mediaType: val.toLowerCase().includes("video") ? "video" : "image"
                          }));
                          if (val.toLowerCase().includes("video")) setImageSrc(""); else setVideoSrc("");
                        }}
                        className={`w-full px-4 py-2 bg-white border ${!formData.adType ? "border-red-300" : "border-gray-200"} rounded-xl text-xs font-bold shadow-sm focus:ring-2 focus:ring-orange-400 outline-none transition-all cursor-pointer`}
                      >
                        <option value="">Choose Format</option>
                        {adTypes.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                      </select>
                    </div>

                    {/* NEW: Ad Type Image Preview Gallery */}
                    {formData.adType && (
                      <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex items-center gap-2 mb-2">
                           <div className="w-1 h-3 bg-orange-500 rounded-full"></div>
                           <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Available Layouts</span>
                        </div>
                        <div className="flex flex-wrap gap-2.5 p-3 bg-slate-50 rounded-2xl border border-slate-100 overflow-x-auto min-h-[80px]">
                          {adTypes.find(t => t.name === formData.adType)?.images?.length > 0 ? (
                            adTypes.find(t => t.name === formData.adType).images.map((img, idx) => (
                              <div 
                                key={idx} 
                                className="relative group/img flex-shrink-0 w-24 h-24 rounded-xl border-2 border-white shadow-sm overflow-hidden bg-white cursor-pointer hover:border-orange-200 transition-all"
                                onClick={() => window.open(`${baseUrl}${img}`, '_blank')}
                              >
                                <img 
                                  src={`${baseUrl}${img}`} 
                                  alt={`${formData.adType} ${idx}`} 
                                  className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-500"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://via.placeholder.com/150?text=Format+Image";
                                  }}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/30 transition-colors flex items-center justify-center gap-2">
                                   <div 
                                      className="p-1.5 bg-white/20 hover:bg-white/40 rounded-lg text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(`${baseUrl}${img}`, '_blank');
                                      }}
                                   >
                                      <FaEye className="w-4 h-4" />
                                   </div>
                                   <div 
                                      className="p-1.5 bg-emerald-500/80 hover:bg-emerald-600 rounded-lg text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
                                      title="Download Layout"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadImage(`${baseUrl}${img}`, `layout-${formData.adType}-${idx}.jpg`);
                                      }}
                                   >
                                      <FaDownload className="w-4 h-4" />
                                   </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="w-full h-20 flex flex-col items-center justify-center text-slate-400 gap-1 opacity-60">
                               <FaImage className="w-5 h-5" />
                               <span className="text-[9px] font-bold uppercase tracking-tight">Default Layout Preview</span>
                            </div>
                          )}
                        </div>
                        <p className="text-[9px] text-slate-400 mt-1.5 font-medium italic">* These are sample layouts for "{formData.adType}". Upload your content below.</p>
                      </div>
                    )}
                  </div>

                  {formData.adType && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="block text-[10px] font-black text-slate-700 uppercase tracking-widest flex items-center justify-between">
                          <span>Media Upload <span className="text-red-500">*</span></span>
                          {(imageSrc || videoSrc) && (
                            <button type="button" onClick={removeFile} className="text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors">
                              <span className="text-[8px]">REMOVE FILE</span>
                            </button>
                          )}
                        </label>
                        <div className={`group relative border-2 border-dashed ${imageSrc || videoSrc ? "border-emerald-200 bg-emerald-50/10" : "border-slate-200 bg-slate-50/50"} rounded-2xl overflow-hidden transition-all min-h-[200px]`}>
                          {!imageSrc && !videoSrc ? (
                            <>
                              <input
                                type="file"
                                id="media-uploader"
                                className="hidden"
                                onChange={handleFileChange}
                                accept={formData.mediaType === "video" ? "video/*" : "image/*"}
                              />
                              <label htmlFor="media-uploader" className="flex flex-col items-center justify-center w-full h-full cursor-pointer p-6 hover:bg-white transition-colors">
                                <div className="p-4 bg-orange-50 rounded-full mb-4 group-hover:scale-110 transition-transform">
                                  <FaCloudUploadAlt className="w-10 h-10 text-orange-600" />
                                </div>
                                <p className="text-xs font-black text-slate-800 mb-1 tracking-tight">CLICK OR DRAG MEDIA</p>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                  {formData.mediaType === "video" ? "MP4 (MAX 100MB)" : "JPG, PNG (MAX 15MB)"}
                                </p>
                              </label>
                            </>
                          ) : (
                            <div 
                              className="relative w-full h-full flex flex-col items-center justify-center bg-slate-900 group min-h-[200px] cursor-pointer"
                              onClick={() => {
                                setFullScreenMedia({ src: imageSrc || videoSrc, type: formData.mediaType });
                                setIsMediaFullScreen(true);
                              }}
                            >
                              {/* Media Type Badge */}
                              <div className="absolute top-3 right-3 z-20">
                                <span className="px-3 py-1 bg-white/20 backdrop-blur-md border border-white/30 rounded-full text-[10px] font-black text-white uppercase tracking-widest flex items-center gap-1.5 shadow-lg">
                                  {formData.mediaType === "video" ? <FaVideo className="text-orange-400" /> : <FaImage className="text-blue-400" />}
                                  {formData.mediaType}
                                </span>
                              </div>

                              {/* Hover Overlay */}
                              <div className="absolute inset-0 z-10 bg-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <div className="bg-white/20 backdrop-blur-md p-4 rounded-full border border-white/30 transform scale-90 group-hover:scale-100 transition-transform">
                                  <FaEye className="w-8 h-8 text-white" />
                                </div>
                              </div>

                              {imageSrc ? (
                                <img src={imageSrc} alt="Preview" className="w-full max-h-[300px] object-contain" />
                              ) : (
                                <video src={videoSrc} controls className="w-full max-h-[300px]" />
                              )}
                              
                              {/* File Info Overlay */}
                              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent z-10">
                                <p className="text-[10px] text-white font-bold truncate">
                                  {formData.mediaType === "video" ? formData.video?.name : formData.image?.name}
                                </p>
                                <p className="text-[8px] text-white/60 uppercase font-black tracking-widest mt-0.5">Click to view full size</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div className="pt-2">
                    <label className="block text-xs font-black text-slate-600 mb-2 flex items-center justify-between uppercase tracking-wider">
                      <span>Description <span className="text-red-500">*</span></span>
                      <span className="text-[8px] text-slate-400 normal-case">Max 500 chars</span>
                    </label>
                    <textarea
                      name="mediaDescription"
                      value={formData.mediaDescription}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Enter description..."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-orange-400 focus:bg-white outline-none transition-all text-xs font-medium shadow-inner"
                    ></textarea>
                  </div>
                </div>
                <div className="px-4 py-3 sm:p-10 bg-slate-50/80 border-t border-slate-100 flex justify-between items-center rounded-b-xl sm:rounded-b-[2rem]">
                  <button type="button" onClick={prevStep} className="px-3 py-2 text-slate-500 font-bold text-sm flex items-center gap-2 hover:text-slate-800 transition-colors">
                    <FaArrowLeft className="w-3 h-3" /> Back
                  </button>
                  <button type="button" onClick={nextStep} className="group relative px-5 py-2.5 sm:px-8 sm:py-4 bg-orange-600 text-white text-sm font-bold rounded-xl sm:rounded-2xl shadow-[0_6px_20px_rgba(234,88,12,0.3)] transition-all hover:translate-y-[-1px] flex items-center gap-2 overflow-hidden">
                    <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 skew-x-[-20deg]" />
                    Review Campaign <FaArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {currentStep === 5 && (
              <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="rounded-xl sm:rounded-[2.5rem] shadow-lg bg-white border border-slate-100 overflow-hidden">
                  {/* Step 5 Header - Back left, stage center, substage nav right */}
                  <div className="px-3 py-2.5 bg-gradient-to-br from-indigo-900 to-slate-900 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => {
                        if (step5Stage === "payment") setStep5Stage("overview");
                        else if (step5Stage === "overview") setStep5Stage("coupon");
                        else prevStep();
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-lg border border-white/20 transition-all"
                    >
                      <FaArrowLeft className="w-2.5 h-2.5" /> Back
                    </button>
                    <div className="text-center">
                      <h2 className="text-xs font-black text-white tracking-tight">
                        {step5Stage === "coupon" ? "Apply Coupon" : step5Stage === "overview" ? "Review" : "Checkout"}
                      </h2>
                      <div className="flex gap-1 justify-center mt-0.5">
                        {["coupon", "overview", "payment"].map((s, idx) => (
                          <div key={s} className={`h-1 rounded-full transition-all duration-300 ${step5Stage === s ? "bg-white w-4" : idx < ["coupon", "overview", "payment"].indexOf(step5Stage) ? "bg-emerald-400 w-2" : "bg-white/30 w-2"}`} />
                        ))}
                      </div>
                    </div>
                    <div className="w-16"></div>{/* spacer to balance */}
                  </div>
                  <div className="p-3 sm:p-8">
                    {step5Stage === "coupon" && (
                      <div className="space-y-5 animate-in fade-in zoom-in-95 duration-500">
                        <div className="text-center space-y-1">
                          <h3 className="text-base font-bold text-slate-800">Do you have a discount code?</h3>
                          <p className="text-slate-500 text-xs">Save big on your first campaign or use seasonal offers.</p>
                        </div>

                        <div className="max-w-md mx-auto relative group">
                          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl blur opacity-10 group-hover:opacity-20 transition duration-1000"></div>
                          <div className="relative flex gap-2 bg-white p-2 rounded-2xl border border-slate-200">
                            <div className="flex-1 flex items-center px-4 gap-3">
                              <FaTicketAlt className="text-slate-400" />
                              <input
                                type="text"
                                name="couponCode"
                                value={formData.couponCode}
                                onChange={handleChange}
                                placeholder="Enter code (e.g. WELCOME50)"
                                className="w-full py-3 bg-transparent font-bold text-slate-700 outline-none placeholder:text-slate-300 placeholder:font-normal"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={applyCouponCode}
                              className={`px-8 py-3 rounded-xl font-black text-sm transition-all duration-300 ${appliedCoupon ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200" : "bg-slate-900 text-white hover:bg-black"}`}
                            >
                              {appliedCoupon ? "Applied!" : "Apply"}
                            </button>
                          </div>
                          {appliedCoupon && (
                            <div className="mt-4 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-600">
                                <FaCheckCircle className="w-4 h-4" />
                              </div>
                              <span className="text-sm font-bold text-emerald-700">Congratulations! Discount applied successfully.</span>
                            </div>
                          )}
                        </div>

                        <div className="pt-4 flex justify-center">
                          <button
                            type="button"
                            onClick={() => setStep5Stage("overview")}
                            className="group px-8 py-3 sm:px-12 sm:py-5 bg-blue-600 text-white font-black text-sm rounded-2xl sm:rounded-3xl shadow-[0_10px_30px_rgba(37,99,235,0.2)] hover:shadow-[0_15px_40px_rgba(37,99,235,0.3)] transition-all hover:-translate-y-0.5 flex items-center gap-3"
                          >
                            Review Your Campaign <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    )}

                    {step5Stage === "overview" && (
                      <div className="space-y-10 animate-in fade-in zoom-in-95 duration-500">
                        {/* Expanded Professional Overview */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {/* Summary Details */}
                          <div className="space-y-6">
                            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Targeting Details</h4>
                              <div className="space-y-3">
                                <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100">
                                  <span className="text-sm text-slate-500 font-medium">Audience</span>
                                  <span className="text-sm font-bold text-slate-800 capitalize">{formData.userType}s</span>
                                </div>
                                <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100">
                                  <span className="text-sm text-slate-500 font-medium">Goal</span>
                                  <span className="text-sm font-bold text-slate-800">{formData.requiredViews.toLocaleString()} Required Views</span>
                                </div>
                                <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100">
                                  <span className="text-sm text-slate-500 font-medium">Location</span>
                                  <span className="text-sm font-bold text-slate-800 truncate max-w-[150px]">{formData.perferenceArea.pfCities?.join(", ") || "All Areas"}</span>
                                </div>
                              </div>
                            </div>

                            <div className="p-6 bg-orange-50/50 rounded-3xl border border-orange-100/50">
                              <h4 className="text-xs font-black text-orange-400 uppercase tracking-widest mb-4">Media Details</h4>
                              <div className="space-y-3">
                                <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100">
                                  <span className="text-sm text-slate-500 font-medium">Media Type</span>
                                  <span className="text-sm font-bold text-slate-800 uppercase flex items-center gap-2">
                                    {formData.mediaType === "video" ? <FaVideo className="text-orange-500" /> : <FaImage className="text-blue-500" />}
                                    {formData.mediaType}
                                  </span>
                                </div>
                                {formData.mediaType === "video" && (
                                  <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100">
                                    <span className="text-sm text-slate-500 font-medium">Duration</span>
                                    <span className="text-sm font-bold text-slate-800">{formData.adLength} Seconds</span>
                                  </div>
                                )}
                                <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100">
                                  <span className="text-sm text-slate-500 font-medium">File Name</span>
                                  <span className="text-sm font-bold text-slate-800 truncate max-w-[150px]">
                                    {formData.mediaType === "video" ? formData.video?.name || "Video File" : formData.image?.name || "Image File"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="p-6 bg-blue-50/50 rounded-3xl border border-blue-100/50">
                              <h4 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-4">Ad Placement</h4>
                              <div className="flex flex-wrap gap-2">
                                {formData.plateforms.map(p => (
                                  <span key={p} className="px-4 py-2 bg-white rounded-full text-xs font-black text-blue-700 border border-blue-100 shadow-sm">{p}</span>
                                ))}
                                {formData.channelType.map(c => (
                                  <span key={c} className="px-4 py-2 bg-indigo-50 rounded-full text-xs font-black text-indigo-700 border border-indigo-100 shadow-sm">{c}</span>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Media & Preview */}
                          <div 
                            className="bg-slate-900 rounded-[2rem] overflow-hidden shadow-2xl relative aspect-square lg:aspect-auto h-full min-h-[300px] cursor-pointer group/preview"
                            onClick={() => {
                              setFullScreenMedia({ src: imageSrc || videoSrc, type: formData.mediaType });
                              setIsMediaFullScreen(true);
                            }}
                          >
                            {/* Media Type Badge */}
                            <div className="absolute top-4 right-4 z-10">
                              <span className="px-3 py-1 bg-white/20 backdrop-blur-md border border-white/30 rounded-full text-[10px] font-black text-white uppercase tracking-widest flex items-center gap-1.5 shadow-lg">
                                {formData.mediaType === "video" ? <FaVideo className="text-orange-400" /> : <FaImage className="text-blue-400" />}
                                {formData.mediaType}
                              </span>
                            </div>

                            {/* Click to Enlarge Hint */}
                            <div className="absolute inset-0 z-10 bg-black/40 opacity-0 group-hover/preview:opacity-100 transition-all duration-300 flex flex-col items-center justify-center gap-2">
                               <div className="p-3 bg-white/20 backdrop-blur-md rounded-full border border-white/30 transform scale-90 group-hover/preview:scale-100 transition-transform duration-500">
                                  <FaEye className="w-6 h-6 text-white" />
                               </div>
                               <span className="text-[10px] font-black text-white uppercase tracking-[0.2em]">Click to expand</span>
                            </div>

                            {imageSrc ? (
                              <img src={imageSrc} alt="Final Preview" className="w-full h-full object-cover opacity-80 group-hover/preview:scale-105 transition-transform duration-700" />
                            ) : videoSrc ? (
                              <video src={videoSrc} className="w-full h-full object-cover opacity-80" autoPlay muted loop />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-white/20">
                                <FaImage className="w-16 h-16" />
                                <span className="mt-4 font-bold uppercase tracking-tighter">No Media Uploaded</span>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent pointer-events-none"></div>
                            <div className="absolute bottom-6 left-6 right-6">
                              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl">
                                <h5 className="text-white text-sm font-bold truncate">{formData.adType || "Sample Campaign"}</h5>
                                <p className="text-white/60 text-xs mt-1 line-clamp-1">{formData.mediaDescription || "No description provided."}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="pt-4 flex justify-center border-t border-slate-100 mt-6">
                          <button
                            type="button"
                            onClick={() => setStep5Stage("payment")}
                            className="group px-8 py-3 sm:px-12 sm:py-5 bg-indigo-600 text-white font-black text-sm rounded-2xl sm:rounded-3xl shadow-[0_10px_30px_rgba(79,70,229,0.2)] hover:shadow-[0_15px_40px_rgba(79,70,229,0.3)] transition-all hover:-translate-y-0.5 flex items-center gap-3"
                          >
                            Proceed to Checkout <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    )}

                    {step5Stage === "payment" && (
                      <div className="space-y-5 animate-in fade-in zoom-in-95 duration-500">
                        {/* Final Pricing Card */}
                        <div className="bg-gradient-to-br from-slate-900 to-black rounded-2xl sm:rounded-[2.5rem] p-5 sm:p-8 text-white shadow-xl sm:shadow-[0_30px_60px_rgba(0,0,0,0.3)] relative overflow-hidden">
                          <div className="relative z-10">
                            <div className="flex justify-between items-center mb-5">
                              <div className="p-2 bg-white/10 rounded-xl border border-white/10">
                                <FaShieldAlt className="w-4 h-4 text-blue-400" />
                              </div>
                              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40">Checkout Summary</span>
                            </div>

                            <div className="space-y-1 mb-5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-xl font-light text-white/60">₹</span>
                                <span className="text-4xl sm:text-6xl font-black tracking-tight">{totalCostWithGST.toLocaleString()}</span>
                              </div>
                              <p className="text-blue-400 text-[9px] font-bold uppercase tracking-widest flex items-center justify-center gap-1.5">
                                Total incl. GST
                              </p>
                            </div>

                            <div className="space-y-4 pt-8 border-t border-white/10">
                              <div className="flex justify-between text-xs font-medium">
                                <span className="text-white/50">Base Campaign Cost</span>
                                <span>₹{totalCost.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-xs font-medium">
                                <span className="text-white/50">GST ({gstRate}%)</span>
                                <span>₹{((totalCost * gstRate) / 100).toLocaleString()}</span>
                              </div>
                              {appliedCoupon && (
                                <div className="flex justify-between text-xs font-black text-emerald-400 bg-emerald-400/10 p-2 rounded-lg">
                                  <span>Coupon Discount</span>
                                  <span>-₹{savedAmount.toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 rounded-full blur-[100px]"></div>
                        </div>

                        {/* Terms & Conditions */}
                        <div className="max-w-md mx-auto">
                          <label className="flex items-start gap-4 p-5 bg-slate-50 rounded-2xl cursor-pointer hover:bg-slate-100 transition-all border border-transparent hover:border-slate-200">
                            <div className="relative flex items-center h-5">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={handleCheckboxChange}
                                className="w-5 h-5 text-blue-600 border-slate-300 rounded-md focus:ring-blue-500 transition-all"
                              />
                            </div>
                            <div className="text-xs font-medium text-slate-600 leading-relaxed">
                              <span className="text-slate-900 font-black">Final Confirmation:</span> I agree to the policy & terms. I confirm that all information provided is accurate and my creative assets comply with guidelines.
                            </div>
                          </label>
                        </div>

                        {/* Side-by-Side Payment Options - 2 col on mobile */}
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); if (validateStep(5)) handleWalletPayment(); }}
                            disabled={isSubmitting || !isChecked}
                            className="group relative px-4 py-4 sm:px-6 sm:py-6 bg-white border-2 border-slate-200 rounded-2xl sm:rounded-[2rem] hover:border-emerald-500 hover:bg-emerald-50/50 transition-all duration-500 flex flex-col items-center gap-3 disabled:opacity-40"
                          >
                            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-500 shadow-inner">
                              <FaWallet className="w-5 h-5" />
                            </div>
                            <div className="text-center">
                              <span className="block font-black text-slate-800 text-xs sm:text-sm group-hover:text-emerald-700 transition-colors">Pay via Wallet</span>
                              <span className="block text-[9px] font-bold text-slate-400 mt-0.5 uppercase">Bal: ₹{walletData.balance.toFixed(0)}</span>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); if (validateStep(5)) handleSubmit(); }}
                            disabled={isSubmitting || !isChecked}
                            className="group relative px-4 py-4 sm:px-6 sm:py-6 bg-white border-2 border-slate-200 rounded-2xl sm:rounded-[2rem] hover:border-blue-600 hover:bg-blue-50/50 transition-all duration-500 flex flex-col items-center gap-3 disabled:opacity-40"
                          >
                            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500 shadow-inner">
                              <FaCreditCard className="w-6 h-6" />
                            </div>
                            <div className="text-center">
                              <span className="block font-black text-slate-800 text-sm group-hover:text-blue-700 transition-colors">Pay Securely</span>
                              <span className="block text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-tighter">Debit/Credit/UPI</span>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sub-navigation for Step 5 */}
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    onClick={() => {
                      if (step5Stage === "payment") setStep5Stage("overview");
                      else if (step5Stage === "overview") setStep5Stage("coupon");
                      else prevStep();
                    }}
                    className="group px-6 py-3 text-slate-400 font-bold text-sm flex items-center gap-2 hover:text-slate-800 transition-all"
                  >
                    <FaArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
                    {step5Stage === "coupon" ? "Edit Campaign Details" : "Go Back"}
                  </button>
                </div>
              </div>
            )}

          </form>
        </div>

        <div className="hidden lg:col-span-1 lg:block">
          <div className="sticky top-6 space-y-6">
            <AdOverview
              formData={formData}
              totalCost={totalCost}
              perDayCost={perDayCost}
              totalCostWithGST={totalCostWithGST}
              savedAmount={savedAmount}
              imageSrc={imageSrc}
              videoSrc={videoSrc}
              gstRate={gstRate}
              pricing={pricing}
              pincode={pincode}
              hasUserInteracted={hasUserInteracted}
            />

            {/* How to Use / Video Guide */}
            <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-[2rem] p-6 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full blur-2xl"></div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-red-50 text-red-500 rounded-2xl border border-red-100 flex items-center justify-center">
                  <FaPlayCircle className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-800 tracking-tight leading-tight">How to Use</h3>
                  <p className="text-slate-400 text-[9px] uppercase font-bold tracking-widest">Campaign Setup Video Guide</p>
                </div>
              </div>

              {/* Video Embed Container */}
              <div className="relative aspect-[9/16] max-h-[360px] mx-auto rounded-2xl overflow-hidden border border-slate-200 shadow-inner bg-slate-950">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/uookHpnxyrY"
                  title="How to use video guide"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              
              <div className="mt-3 text-center">
                <a
                  href="https://www.youtube.com/shorts/uookHpnxyrY"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-red-600 font-bold hover:underline"
                >
                  Watch directly on YouTube <FaArrowRight className="w-2.5 h-2.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Price Strip - visible only on mobile, hidden when submitting or modal is open */}
      {!isSubmitting && !showConfirmModal && isMobilePriceStripVisible && currentStep < 5 && (
        <div className="fixed bottom-0 left-0 w-full bg-white border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50 block lg:hidden">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setIsMobilePriceStripVisible(false)}
                className="mr-3 p-1 text-gray-400 hover:text-gray-600"
                title="Hide price strip"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <div>
                <span className="text-xs text-gray-600">Total:</span>
                <span className="text-base font-bold text-blue-600 ml-1">
                  ₹
                  {hasUserInteracted
                    ? (totalCostWithGST ?? 0).toFixed(2)
                    : "0.00"}
                </span>
              </div>
            </div>
            <button
              type="button"
              disabled={isSubmitting || isFormIncomplete}
              onClick={validateForm}
              className={`px-4 py-2 rounded-md font-medium text-white ${isSubmitting || isFormIncomplete
                ? "bg-gray-400 cursor-not-allowed opacity-70"
                : "bg-blue-600 hover:bg-blue-700"
                }`}
            >
              {isSubmitting ? "Processing..." : "Submit"}
            </button>
          </div>
        </div>
      )}

      {/* Show Price Strip Button - appears when strip is hidden */}
      {!isSubmitting && !showConfirmModal && !isMobilePriceStripVisible && currentStep < 5 && (
        <button
          type="button"
          onClick={() => setIsMobilePriceStripVisible(true)}
          className="fixed bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg z-50 block lg:hidden"
          title="Show price strip"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
            />
          </svg>
        </button>
      )}
      {/* Uploading Progress Overlay */}
      {isPostingAd && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-[2.5rem] p-8 sm:p-12 w-full max-w-lg shadow-2xl border border-white/20 relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-100 rounded-full blur-3xl opacity-50"></div>
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-indigo-100 rounded-full blur-3xl opacity-50"></div>

            <div className="relative z-10 space-y-8 text-center">
              {/* Animated Icon Container */}
              <div className="relative w-24 h-24 mx-auto">
                <div className="absolute inset-0 bg-blue-600/10 rounded-full animate-ping"></div>
                <div className="relative bg-white rounded-full w-24 h-24 flex items-center justify-center shadow-lg border border-slate-50">
                  <FaCloudUploadAlt className="w-10 h-10 text-blue-600 animate-bounce" />
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-2xl font-black text-slate-800 tracking-tight">Uploading Campaign</h3>
                <p className="text-slate-500 font-medium text-sm">Please wait while we secure your media and assets...</p>
              </div>

              {/* Progress Container */}
              <div className="space-y-4">
                <div className="relative h-4 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200 shadow-inner">
                  <div
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-300 ease-out shadow-[0_0_10px_rgba(37,99,235,0.4)]"
                    style={{ width: `${uploadProgress}%` }}
                  >
                    <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.2)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.2)_50%,rgba(255,255,255,0.2)_75%,transparent_75%,transparent)] bg-[length:24px_24px] animate-[progress-bar-stripes_1s_linear_infinite]"></div>
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs font-black tracking-widest uppercase">
                  <span className="text-blue-600">{uploadProgress}% Complete</span>
                  <span className="text-slate-400">Syncing to Server</span>
                </div>
              </div>

              {/* Security Badge */}
              <div className="pt-4 flex items-center justify-center gap-2">
                <div className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border border-emerald-100">
                  <FaShieldAlt className="w-3 h-3" />
                  Encrypted Transfer
                </div>
              </div>
            </div>

            <style jsx="true">{`
              @keyframes progress-bar-stripes {
                from { background-position: 24px 0; }
                to { background-position: 0 0; }
              }
            `}</style>
          </div>
        </div>
      )}
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleSubmit}
        onWalletPay={handleWalletPayment}
        formData={formData}
        plateforms={plateforms}
        totalCost={totalCost}
        totalCostWithGST={totalCostWithGST}
        savedAmount={savedAmount}
        gstRate={gstRate}
        appliedCoupon={appliedCoupon}
        imageSrc={imageSrc}
        videoSrc={videoSrc}
        isPostingAd={isPostingAd}
        setFullScreenMedia={setFullScreenMedia}
        setIsMediaFullScreen={setIsMediaFullScreen}
      />

      {/* Media Lightbox */}
      <MediaLightbox />
    </div>
  );
};

export default PostAdvertisement;
